import { lexiconTrigrams } from '../lexicon/lexicon-trigram'
import { usePagePractice } from './page-practice'
import { recordPageView } from './record-page-view'
import { usePageViews } from './page-views'
import { FailedPageItems } from '../dead-letter-queue/page-placeholders'
import { DatabaseSidebar } from '../databases/database-sidebar'
import { useNewDatabase } from '../databases/new-database'
import { WorkspaceSearch } from '../search/workspace-search'
import { useSignOut } from '../shell/app'
import { commitPageEdits } from '../db/commit-page-edit'
import { refreshPageSearch } from '../db/page-search-projection'
import type { PageScalarFields } from '../db/page-document'
import { effectiveTranslatable } from '../translation/effective-translatable'
import { ReplicaTransportError } from '@w/db-turso-browser-adapter/worker'
import { useMediaQuery, withoutActWarning } from '@w/utils-fe'
import { queryOptions as dbQueryOptions } from '@w/react-query/db'
import { s } from '@w/schema'
import { adoptPageCommands } from '../router/page-commands'
import { cardReadColumns, cardVisibleTo, learnerCards } from '../db/question-card-triggers'
import { actOnCard, requestPredictedCell } from '../db/learner-actions'
import { QuestionCurrentSettings } from '../cards/question-current-settings'
import { WorkspaceSyncAlerts, WorkspaceSyncButton, WorkspaceSyncLine, WorkspaceSyncStatus } from '../cards/workspace-sync-status'
import { Avatar } from '@w/ui/components/avatar'
import { writeDocument } from '@w/content-blocks-editor/doc-bindings-for-page'
import { useUserSpaces } from '../router/authenticated-layout'
import type { Tel } from '@w/tel'
import { nativeTongueFor } from '../translation/native-language'
import { acceptCommentThread, addPageCommentThread, applyTranslation, commentAnchorText, editCommentMessage, getBlockCommentThreads, getPageCommentThreads, removeCommentMessage, removeCommentThread, reopenCommentThread, replyCommentThread, upsertCommentThread, type Blocks } from '@w/content-blocks'
import { narratePage } from '../listen/page-narration'
import { PageListenBar } from '../listen/page-listen-bar'
import { planSpeech, type SpeechPlan } from '../listen/page-speech'
import { speechLabels } from '../listen/speech-labels'
import { createSpeechSession, type SpeechSession } from '../listen/speech-session'
import { Doc } from '@w/crdt'
import { PageSharing } from './page-sharing'
import { usePageErrors } from './page-errors'
import { receivedLiveVersion, containsVersion, usePageWrites } from './page-writes'
import { PageFind } from './page-find'
import { PagePeople, PagePresence } from './page-collaborators'
import { RealtimeProvider, useLiveEdits } from '@w/realtime'
import { WorkspaceClosedError, type SpaceResource } from '../router/spaces-resources'
import { RpcError } from '@w/rpc'
import { ErrorBoundary } from '@w/ui/system/error-boundary'
import { pageRouteParams } from '../router/page-route-params'
import type { PageRef } from '@w/content-blocks/schema'
import { contentResources, SpaceQueries, spaceTreeOptions, spaceBacklinksOptions, useSpacesView, prepareSpaceSearch, mergeSearchHits } from '../db/spaces-queries'
import type { PageSearchReference } from '../db/page-backlinks'
import { getAffectedBlocks, pageDocument, type Change } from '@w/content-blocks-editor/doc-bindings-for-page'
import { isRoomPublishable } from '../db/live-edit-scope'
import { createPageCommitBuffer, type FlushMode, type WriteRun } from '../db/page-commit-buffer'
import { writePageUnload } from '../db/page-unload-journal'
import { editEntryQuestions } from '../lexicon/entry-question-edit'
import { writeWorkspace } from '../db/workspace-write'
import { isDev, unknownErrorToError } from '@w/utils'
import { ErrorDetails } from '@w/ui/components/error-details'
import { ImageLoadingProvider } from '@w/ui/components/image'
import { storedMediaPaths } from '../media/media-paths'
import { usePreparedMedia } from '../media/prepared-media'
import { feEnv } from '@w/utils-fe/env'
import { useScrollContainer, isFocusWithin } from '@w/utils-fe'
import { onVisibilityChange } from '@w/runtime/visibility'
import { focusPageTitle, schedulePageTitleFocus, selectUntouchedTitleOnClick } from './page-editor'
import { blockId, type Block } from '@w/content-blocks/src/block'
import {
  BlockEditor,
  createBucketCdc,
  type BlockEditorHandle,
  type BlockReviewSelection,
  type SearchPages,
} from '@w/content-blocks-editor'
import { useMutation, useQuery, useSuspenseQuery, type UseQueryResult } from '@w/react-query'
import {
  type ReactNode,
  Fragment,
  createContext,
  use,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { crdt, type Version } from '@w/crdt'
import { ulid } from '@w/utils'
import { useDebounce } from '@w/utils-fe'
import { createPageCommand } from '../router/page-commands'
import { commitWorkspacePageEdit } from '../db/page-write'
import type { FlashcardsFeRouterCtx } from '../router/flashcards-fe.router-ctx'
import { getFlashcardsMediaDir } from '../server/media/flashcards-media-be'
import { pageBlocks } from './page-blocks'
import { translatedPageTitle } from '../translation/page-title-unit'
import { pageViewOptions } from '../router/page-view'
import { useTranslation } from '../i18n'
import {
  useNavigate,
  useParams,
  useRouterContext,
  useSearch,
} from '../router/flashcards-fe.router-bindings'
import { claimPageDoc, type PageRow, type PageMeta } from '../db/page-read'
import type { FlashcardsUser } from '../flashcards-auth'
import { Button } from '@w/ui/components/button'
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from '@w/ui/components/empty'
import { Shape } from '@w/ui/components/shape'
import { sunny } from '@w/ui/components/shapes'
import { Dialog, DialogPopup, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@w/ui/components/dialog'
import { Input } from '@w/ui/components/input'
import { abortOnNavigation, DirectoryRefreshError } from '../router/flashcards-rpc-options'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@w/ui/components/table'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from '@w/ui/components/sidebar'
import {
  BookMarkedIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloudIcon,
  Edit2Icon,
  FileTextIcon,
  HeadphonesIcon,
  LayersIcon,
  LanguagesIcon,
  LockIcon,
  MoreHorizontalIcon,
  MessageSquareIcon,
  SearchIcon,
  Share2Icon,
  UsersIcon,
  LoaderCircleIcon,
  LogOutIcon,
  PlusIcon,
  SettingsIcon,
  Trash2Icon,
  WandIcon,
} from '@w/ui/components/icon'
import { IconButton } from '@w/ui/components/icon-button'
import { useIsMobile } from '@w/ui/hooks/use-mobile'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@w/ui/components/collapsible'
import { Menu, MenuTrigger, MenuPopup, MenuRadioGroup, MenuRadioItem, MenuSeparator, MenuGroup, MenuItem, MenuCheckboxItem } from '@w/ui/components/menu'
import {
  Command,
  CommandCollection,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandPanel,
} from '@w/ui/components/command'
import { adoptEntry } from '../lexicon/lexicon-adoption'
import { EntryEditor } from '../lexicon/entry-editor'
import type { PurgeOptions } from '../lexicon/entry-editor'
import { rankHits, lexiconPageSearchQuery, lexiconPageHit } from '../lexicon/lexicon-search'
import {
  adoptEntryContent,
  entryMediaPaths,
  translationFor,
} from '../lexicon/lexicon-model'
import { pageKindPolicy } from '../db/schema'
import type {
  CachedEntry,
  TranslationLanguage,
  LexiconEntry,
  LexiconHit,
} from '../lexicon/lexicon-model'
import { VocabularyProgressList } from '../lexicon/vocabulary-progress-list'
import {
  vocabularyProgress,
  type AdoptedLemmaRow,
  type FrequencyRow,
  type LemmaStatus,
} from '../lexicon/vocabulary-progress'

// Module scope (Zod: define schemas once): inline schemas paid construction on every call.
const stringRecordSchema = s.record(s.string(),s.string())
const transcriptionRowSchema = s.object({ id: s.string(), transcription: s.string(), translations: s.transform(s.string(), value => stringRecordSchema.parse(JSON.parse(value))) })
const placementSchema = s.object({ pageId: s.string(), parentPageId: s.string() })

// Chosen to sit below the ~300ms threshold where a search box starts to feel
// laggy, while still collapsing a typed word (5-8 keystrokes at typing speed)
// into a single remote round trip instead of one per keystroke.
const LEXICON_REMOTE_SEARCH_DEBOUNCE_MS = 200

interface FlashcardsPageData {
  user: FlashcardsUser
  workspaceId: string
  workspaces: Array<{
    id: string
    name: string
  }>
  page: PageRow
  pages: PageMeta[]
  blocks: Block[]
}

interface FlashcardsPageTreeItem {
  kind: PageMeta['kind']
  spaceId: string
  id: string
  title: string
  parentId: string | null
  children: FlashcardsPageTreeItem[]
}

/** The directory route also exists when the workspace has no live page to open. */
export function PagesDirectory() {
  const ctx = useRouterContext()
  const params = useParams({ from: '/$workspaceId/spaces/$spaceId/pages' })
  const spaces = useUserSpaces()
  const { data: view, error } = useSpacesView(spaces, params.workspaceId, ctx.queryClient)
  if (error) throw error
  if (!view) return null
  const resource = contentResources(view).find(value => value.space.id === params.spaceId)
  if (!resource) throw new Error('Page unavailable')
  return <PagesDirectoryContent resource={resource} />
}

function PagesDirectoryContent({ resource }: { resource: SpaceResource }) {
  const navigate = useNavigate()
  const params = useParams({ from: '/$workspaceId/spaces/$spaceId/pages' })
  const { data: pages, error } = useQuery(spaceTreeOptions(resource), resource.queryClient)
  const { t } = useTranslation('page')
  if (error) throw error
  return <main className="bg-surface-1 text-on-surface flex min-h-screen flex-col gap-4 p-6">
    <h1 className="text-lg font-medium">{t('pagesLabel')}</h1>
    <WorkspaceSyncStatus workspaceId={resource.space.workspaceId} />
    {pages?.length === 0 ? <p>{t('treeEmpty')}</p> : null}
    {pages?.map(page => <Button key={page.id} data-testid={`flashcards-page-link-${page.id}`} onClick={() => {
      navigate({ to: '/$workspaceId/spaces/$spaceId/pages/$pageId', params: { ...params, pageId: page.id } })
    }}>{page.title}</Button>)}
    <Button data-testid="flashcards-open-cards" onClick={() => {
      navigate({ to: '/$workspaceId/cards', params: { workspaceId: params.workspaceId } })
    }}>{t('cardsButton')}</Button>
  </main>
}

export function PageLayout() {
  return <AppLayout><Page /></AppLayout>
}

export function AppLayout({ children }: { children: ReactNode }) {
  return (
    <SidebarProvider className="bg-surface-1 text-on-surface min-h-screen">
      {/* No scroll container here: one that never scrolls would pin the sticky
          header and the listening bar to the content, not the viewport. `clip`
          is not a scroll container, and keeps wide content from widening the page. */}
      <div className="flex min-w-0 flex-1 flex-col overflow-x-clip">
        {children}
      </div>
    </SidebarProvider>
  )
}

function Page() {
  const navigate = useNavigate()
  const ctx = useRouterContext()
  const params = useParams({ from: '/$workspaceId/spaces/$spaceId/pages/$pageId' })
  const workspaceId = params.workspaceId
  const [shareOpen, setShareOpen] = useState(false)
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false)
  const workspaceSelection = useRef<AbortController | null>(null)
  function beginWorkspaceSelection() {
    workspaceSelection.current?.abort()
    const selection = new AbortController()
    workspaceSelection.current = selection
    return selection
  }
  const spaces = useUserSpaces()
  const { data: view, error } = useSpacesView(spaces, workspaceId, ctx.queryClient)
  if (error) throw error
  const resources = view ? contentResources(view) : []
  const resource = resources.find((value) => value.space.id === params.spaceId)
  const stats = view?.resources.find(
    (value) => value.space.id === params.spaceId,
  )
  return (
    <PageExpansionProvider>
    <RealtimeProvider client={ctx.realtime?.(workspaceId) ?? null}>
      {/* An open dialog stays mounted while a directory refresh replaces resources. */}
      <PageSharing
        key={`${params.spaceId}:${params.pageId}`}
        source={{ spaceId: params.spaceId, pageId: params.pageId }}
        workspaceId={workspaceId}
        resource={stats}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
      {view ? <SpaceQueries key={JSON.stringify(resources.map(value => [value.space.id, value.connectionId]))} resources={resources} options={spaceTreeOptions}>
        {(trees) => (
          <SpaceQueries
            resources={resources}
            options={(resource) =>
              spaceBacklinksOptions(resource, {
                spaceId: params.spaceId,
                pageId: params.pageId,
              })
            }
          >
            {(links) => (
              <>
                {view.unavailable.map(({ space }) => (
                  <p role="alert" key={space.id}>
                    Incomplete workspace: {space.name} unavailable
                  </p>
                ))}
                {[{ kind: 'tree', results: trees }, { kind: 'backlinks', results: links }]
                  .flatMap(({ kind, results }) => results.filter(result => result.error).map(result => (
                    <p role="alert" key={JSON.stringify([kind, result.resource.space.id])}>
                      Incomplete workspace: {result.resource.space.name}:{' '}
                      {result.error?.message}
                    </p>
                  )))}
                <div className="flex min-h-0 min-w-0 flex-1">
                  {!resource || !stats ? (
                    <main className="flex min-w-0 flex-1 flex-col gap-4 p-6">
                      <p>Page unavailable</p>
                      {resource ? <aside aria-label="Unavailable pages"><FailedPageItems resource={resource} /></aside> : null}
                      {trees
                        .flatMap((result) => result.data ?? [])
                        .map((row) => (
                          <Button
                            key={JSON.stringify([row.spaceId, row.id])}
                            data-testid={`flashcards-page-link-${row.id}`}
                            onClick={() => {
                              navigate({
                                to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
                                params: pageRouteParams(ctx, row.spaceId, row.id),
                              })
                            }}
                          >
                            {row.title}
                          </Button>
                        ))}
                    </main>
                  ) : (
                    <PageFallbackResource value={resource}>
                    <PageFallbackTrees value={trees}>
                    <ErrorBoundary
                      key={JSON.stringify([spaces.userId, params.spaceId, params.pageId, resource.connectionId])}
                      fallback={PageFallback}
                      onError={error => {
                        if (isClosedPageResource(error)) return
                        // Report inside a span so the exported error names the failed page load.
                        const input = { workspaceId, spaceId: params.spaceId, pageId: params.pageId }
                        void ctx.tel.span({ name: 'page.render', target: 'job', input }, pageTel => { pageTel.error(error, { operation: 'page.render', ...input }) })
                      }}
                    >
                      <PageContent
                        workspaceMenu={{ open: workspaceMenuOpen, setOpen: setWorkspaceMenuOpen }}
                        beginWorkspaceSelection={beginWorkspaceSelection}
                        accessLost={view.accessLost}
                        onShare={() => { setShareOpen(true) }}
                        resource={resource}
                        stats={stats}
                        resources={resources}
                        pages={trees.flatMap((result) => result.data ?? [])}
                        backlinks={links.flatMap((result) => result.data ?? [])}
                      />
                    </ErrorBoundary>
                    </PageFallbackTrees>
                    </PageFallbackResource>
                  )}
                </div>
              </>
            )}
          </SpaceQueries>
        )}
      </SpaceQueries> : null}
    </RealtimeProvider>
    </PageExpansionProvider>
  )
}

function isClosedPageResource(error: unknown) {
  return error instanceof WorkspaceClosedError || error instanceof RpcError && error.status === 403
}

/** Keep directory and sync subscribers alive after an individual page read fails.
 * Unknown failures retain diagnostics and are reported by the page boundary. */
// A stable fallback type: an inline arrow is a new component on every Page
// render, which remounts the fallback and drops clicks on its links.
const PageFallbackResource = createContext<SpaceResource | null>(null)
const PageFallbackTrees = createContext<readonly { data?: PageMeta[] }[]>([])

// Owns page-tree expansion so a chevron toggle rerenders only the tree nodes,
// never the page or editor rendered as this provider's children.
const PageExpansion = createContext<Record<string, boolean> | null>(null)
const SetPageExpansion = createContext<((update: (previous: Record<string, boolean>) => Record<string, boolean>) => void) | null>(null)

export function PageExpansionProvider({ children }: { children: ReactNode }) {
  const [expandedPages, setExpandedPages] = useState<Record<string, boolean>>({})
  return <SetPageExpansion value={setExpandedPages}><PageExpansion value={expandedPages}>{children}</PageExpansion></SetPageExpansion>
}

function usePageExpansion() {
  const expandedPages = use(PageExpansion)
  const setExpandedPages = use(SetPageExpansion)
  if (!expandedPages || !setExpandedPages) throw new Error('PageTreeNode must render inside PageExpansionProvider')
  return { expandedPages, toggle: (id: string, expanded: boolean) => { setExpandedPages(previous => ({ ...previous, [id]: expanded })) } }
}

function PageFallback({ error, resetErrorBoundary }: { error: unknown; resetErrorBoundary?: () => void }) {
  const resource = use(PageFallbackResource)
  const trees = use(PageFallbackTrees)
  if (!resource) throw new Error('Page fallback outside its page')
  return <ClosedPageResource error={error} resetErrorBoundary={resetErrorBoundary} resource={resource} pages={trees.flatMap(result => result.data ?? [])} />
}

function ClosedPageResource({ error, resetErrorBoundary, resource, pages }: { error: unknown; resetErrorBoundary?: () => void; resource: SpaceResource; pages: PageMeta[] }) {
  const ctx = useRouterContext()
  const navigate = useNavigate()
  const params = useParams({ from: '/$workspaceId/spaces/$spaceId/pages/$pageId' })
  if (isClosedPageResource(error)) return <main className="p-6"><p>Page unavailable</p></main>
  return <main className="flex min-w-0 flex-1 flex-col gap-4 p-6">
    <WorkspaceSyncStatus workspaceId={resource.space.workspaceId} />
    <p role="alert">Page unavailable</p>
    <ErrorDetails error={error} compact />
    <Button data-testid="page-reader-retry" onClick={() => {
      resource.queryClient.removeQueries({ predicate: query => query.state.status === 'error' && query.options.queryKey.includes(params.pageId) })
      resetErrorBoundary?.()
    }}>Retry page</Button>
    <nav aria-label="Pages" data-testid="flashcards-page-tree" className="flex flex-col gap-2">
      {pages.filter(row => row.id !== params.pageId || row.spaceId !== params.spaceId).map(row => <Button key={JSON.stringify([row.spaceId, row.id])} data-testid={`flashcards-page-link-${row.id}`} onClick={() => {
        navigate({ to: '/$workspaceId/spaces/$spaceId/pages/$pageId', params: pageRouteParams(ctx, row.spaceId, row.id) })
      }}>{row.title}</Button>)}
    </nav>
    <Button data-testid="flashcards-open-cards" onClick={() => { navigate({ to: '/$workspaceId/cards', params: { workspaceId: params.workspaceId } }) }}>Cards</Button>
    <Button data-testid="flashcards-open-training" onClick={() => { navigate({ to: '/$workspaceId/training', params: { workspaceId: params.workspaceId } }) }}>Training</Button>
  </main>
}

type PageView = Awaited<ReturnType<ReturnType<typeof pageViewOptions>['queryFn']>>
const PageViewContext = createContext<PageView | null>(null)
function usePageView() {
  const view = use(PageViewContext)
  if (!view) throw new Error('Page view unavailable')
  return view
}

/** A server answer, or null when the server is unreachable (the transport rejects with a TypeError), so the caller may serve the device cache (cell B.3). Any other rejection propagates. */


function AdoptBumpError(props: { pageId: string }) {
  const routerCtx = useRouterContext()
  const { data } = useQuery(routerCtx.adoptBumpErrors.options(props.pageId), routerCtx.queryClient)
  return data?.error === undefined ? null : (
    <div role="alert" data-testid="lexicon-adopt-bump-error" className="px-4 md:px-6">
      <ErrorDetails error={data.error} compact />
    </div>
  )
}

/** The open page stays listed while the tree query catches up, unless it was soft-deleted (then it leaves the sidebar). */
function _withOpenPage(pages: PageMeta[], open: PageRow) {
  if (open.deletedAt !== null && open.deletedAt !== undefined) return pages
  return pages.some((item) => item.id === open.id && item.spaceId === open.spaceId) ? pages : [{ ...open, language: open.content.kind === 'language' ? open.content.language : null, entryId: open.content.kind === 'lexiconEntry' ? open.content.entry.id : null }, ...pages]
}

function PageContent(props: { workspaceMenu: { open: boolean; setOpen: (open: boolean) => void }; beginWorkspaceSelection: () => AbortController; accessLost: boolean; onShare: () => void; pages: PageMeta[]; backlinks: PageSearchReference[]; resource: SpaceResource; stats: SpaceResource; resources: SpaceResource[] }) {
  const ctx = useRouterContext()
  const navigate = useNavigate()
  const params = useParams({ from: '/$workspaceId/spaces/$spaceId/pages/$pageId' })
  const { data: view } = useSuspenseQuery(pageViewOptions(ctx, params, props.resource), props.resource.queryClient)
  const { data } = useSuspenseQuery(view.pageOptions, props.resource.queryClient)
  return data[0] ? <PageViewContext value={view}><AvailablePageContent {...props} /></PageViewContext> : <main className="flex min-w-0 flex-1 flex-col gap-4 p-6">
    <WorkspaceSyncStatus workspaceId={props.resource.space.workspaceId} />
    <p>Page unavailable</p>
    <aside aria-label="Unavailable pages"><FailedPageItems resource={props.resource} /></aside>
    {props.pages.map(row => <Button key={JSON.stringify([row.spaceId, row.id])} data-testid={`flashcards-page-link-${row.id}`} onClick={() => {
      navigate({ to: '/$workspaceId/spaces/$spaceId/pages/$pageId', params: pageRouteParams(ctx, row.spaceId, row.id) })
    }}>{row.title}</Button>)}
  </main>
}

type PageTitleEdit = { title: string; version: Version }
type PageBufferedEdit = Change | PageTitleEdit

function createDocumentSession(page: Pick<PageRow, 'id' | 'data' | 'spaceId'>, workspaceId: string, files: Parameters<typeof createBucketCdc>[0]['files'], user: string) {
  const doc = claimPageDoc(page.data, user)
  return { id: page.id, doc, initialSnapshot: doc.encodeSnapshot().bytes,
    commits: null as ReturnType<typeof createPageCommitBuffer<PageBufferedEdit>> | null,
    /** Unload-journal records this session wrote and no commit has covered yet, oldest first. */
    unload: [] as string[],
    handle: null as BlockEditorHandle | null,
    fileHandler: createBucketCdc({
      doc, pageId: page.id, workspaceId: page.spaceId,
      assetDir: getFlashcardsMediaDir({ pageId: page.id, workspaceId }),
      files, onChange() {},
    }).fileHandler,
  }
}

function commentSourceMap(blocks: Blocks) {
  const ranges = new Map<string, { startLine: number; endLine: number }>()
  let line = 0
  const visit = (list: Blocks) => {
    for (const block of list) {
      if (block.type === 'comments') continue
      if (['p', 'h', 'pre', 'td', 'li'].includes(block.type)) {
        line += 1
        ranges.set(block.id, { startLine: line, endLine: line })
      }
      if ('children' in block && Array.isArray(block.children)) visit(block.children as Blocks)
    }
  }
  visit(blocks)
  return ranges
}

/** Comment threads of the open page's live Doc; owns their state so editor commits re-render only the editor, not the page shell. */
function PageComments({ doc, enabled, read, unsent, children }: {
  doc: Doc
  enabled: boolean
  read: () => Blocks
  unsent: UnsentComment[]
  children: (comments: { blocks: Blocks; onStateChange: (state: { doc: Doc }) => void; refresh: (blocks: Blocks) => void }) => ReactNode
}) {
  const [commentDocument, setCommentDocument] = useState(() => ({ doc, blocks: read() }))
  if (commentDocument.doc !== doc) setCommentDocument({ doc, blocks: read() })
  const blocks = commentDocument.doc === doc ? commentDocument.blocks : pageDocument(doc).content.children as Blocks
  // A write that failed after its local commit still lands; such a comment is shown by its thread, not as unsent.
  const saved = savedCommentIds(blocks)
  const shown = enabled ? unsent.filter(comment => !saved.has(comment.id)) : []
  return <>
    {shown.map(comment => <div key={comment.id} data-testid="page-comment-unsent" className="text-body-medium mb-2 flex items-baseline gap-2">
      <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">{comment.body}</span>
      <span className="text-error shrink-0 text-xs">Not sent · <button type="button" data-testid="page-comment-unsent-retry" className="underline" onClick={comment.retry}>Retry</button></span>
    </div>)}
    {children({
      blocks,
      onStateChange: state => { if (enabled) setCommentDocument({ doc, blocks: pageDocument(state.doc).content.children as Blocks }) },
      refresh: next => { setCommentDocument({ doc, blocks: next }) },
    })}
  </>
}

/** A new comment whose write failed: its text stays on the page until a retry saves it (spec :uc[unsent-comment-retry]). */
type UnsentComment = { id: string; body: string; retry: () => void }

function newComment(authorId: string, body: string) {
  return { id: crypto.randomUUID(), authorId, body, createdAt: Date.now() }
}

function savedCommentIds(blocks: Blocks) {
  return new Set([...getPageCommentThreads(blocks), ...getBlockCommentThreads(blocks).map(({ thread }) => thread)].flatMap(thread => thread.comments.map(comment => comment.id)))
}

function AvailablePageContent({ pages, backlinks, resource, stats, resources, onShare, accessLost, beginWorkspaceSelection, workspaceMenu }: { workspaceMenu: { open: boolean; setOpen: (open: boolean) => void }; beginWorkspaceSelection: () => AbortController; accessLost: boolean; onShare: () => void; pages: PageMeta[]; backlinks: PageSearchReference[]; resource: SpaceResource; stats: SpaceResource; resources: SpaceResource[] }) {
  const navigate = useNavigate()
  const routerCtx = useRouterContext()
  const isMobile = useIsMobile()
  const largeFinePointer = useMediaQuery('(min-width: 1200px) and (pointer: fine)')
  const { auth, testSupport } =
    routerCtx
  const { errors, report, track, saveError } = usePageErrors()
  const [unsentComments, setUnsentComments] = useState<UnsentComment[]>([])
  // Event handlers read the latest list; render reads the state.
  const unsentRef = useRef<UnsentComment[]>([])
  const resending = useRef(new Set<string>())
  function setUnsent(next: UnsentComment[]) { unsentRef.current = next; setUnsentComments(next) }
  const reportFromEffect = useEffectEvent(report)
  const blockLinkAttempt = useRef(0)
  const [bucket] = useState(() => routerCtx.bucket)
  const [user] = useState(() => auth.getUser())
  const loaderData = usePageView()
  const { qid, blockId: linkedBlockId, focusTitle, nav, q } = useSearch({
    from: '/$workspaceId/spaces/$spaceId/pages/$pageId',
  })
  const dismissVocabularyPreview = useRef<(() => void) | null>(null)
  const titleFocusPageIdRef = useRef<string | null>(focusTitle ?? null)
  const { data: rulePages } = useSuspenseQuery(loaderData.rulePagesOptions, loaderData.queryClient)
  const { data: subscribedPageRows } = useSuspenseQuery(loaderData.pageOptions, loaderData.queryClient)
  const loadedPageRow = subscribedPageRows[0]
  if (!loadedPageRow) throw new Error('Page unavailable')
  const { data: workspaceDirectory } = useSuspenseQuery(loaderData.workspacesOptions)
  const workspaces = workspaceDirectory.workspaces
  const commentAuthors = new Map(workspaceDirectory.memberships
    .filter(member => member.workspaceId === resource.space.workspaceId)
    .map(member => [member.userId, member]))
  function resolveCommentAuthor(authorId: string) {
    const profile = commentAuthors.get(authorId) ?? (authorId === user.id ? user : undefined)
    return {
      name: profile?.name ?? profile?.email ?? 'Former member',
      avatarUrl: profile?.avatar ?? undefined,
    }
  }
  const currentCommentAuthor = resolveCommentAuthor(user.id)
  const { data: teamspaces } = useSuspenseQuery(loaderData.teamspacesOptions, loaderData.queryClient)
  const { data: dueCountRows } = useSuspenseQuery(loaderData.dueCountOptions, loaderData.statsQueryClient)
  const { data: pageFlashcards } = useSuspenseQuery(
    loaderData.pageFlashcardsOptions, loaderData.statsQueryClient,
  )
  const dueCount = dueCountRows[0]?.count ?? 0

  const { data: placements } = useSuspenseQuery(dbQueryOptions(
    resource.db.query`SELECT ${resource.db.pageRelations.toPageId} AS pageId, ${resource.db.pageRelations.fromPageId} AS parentPageId FROM ${resource.db.pageRelations}
      WHERE ${resource.db.pageRelations.type.eq('child')}`,
    placementSchema,
  ), resource.queryClient)
  const visiblePages = _withOpenPage(pages, loadedPageRow)
  // The tree derives from its own sources, never from the page object or `visiblePages`: their mutable range spans later
  // hooks, so React Compiler rebuilt a tree from them on every page row change (a remote edit) and AppNavigation
  // re-rendered with a deep-equal tree. `_withOpenPage` returns `pages` itself while the open page is listed.
  const sidebarPages = loadedPageRow.kind === 'lexiconEntry' ? pages : _withOpenPage(pages, loadedPageRow)
  const pageTree = buildPageTree(sidebarPages.filter(item => item.kind !== 'databaseRow'), placements)
  const loadedPage: FlashcardsPageData = {
    user,
    workspaceId: loadedPageRow.spaceId,
    workspaces,
    page: loadedPageRow,
    pages: visiblePages,
    blocks: pageBlocks(loadedPageRow.content),
  }
  // One instance per page (ErrorBoundary key in PageView), so the session only changes on an authoritative rewind.
  const [documentSession, setDocumentSession] = useState(() => createDocumentSession(loadedPageRow, resource.space.workspaceId, bucket, resource.userId))
  const [draft, setDraft] = useState<{ value: string; token: object } | null>(null)
  const page = draft ? { ...loadedPage, page: { ...loadedPage.page, title: draft.value } } : loadedPage
  const readableParentId = page.pages.some(candidate => candidate.id === page.page.parentId && candidate.spaceId === page.workspaceId) ? page.page.parentId : null
  const scroll = useScrollContainer()
  const { headerRef, isAtTop } = scroll
  const activePageRef = useRef(page)
  // Async mutation callbacks read the latest committed page, never a speculative render.
  useLayoutEffect(() => { activePageRef.current = page })
  const { t } = useTranslation('page')
  const translationLanguage = nativeTongueFor(user.settings.lexicon?.nativeLanguage, routerCtx.defaultNativeLanguage)
  const [previousTranslations, setPreviousTranslations] = useState(() => ({ pageId: loadedPageRow.id, language: translationLanguage, version: loadedPageRow.updatedAt.getTime(), data: loadedPageRow.translationRows, rows: loadedPageRow.translationRows }))
  const sameTranslationVisit = previousTranslations.pageId === loadedPageRow.id && previousTranslations.language === translationLanguage
  const currentTranslationRows = loadedPageRow.translationRows
  const changedTranslationSource = previousTranslations.version !== loadedPageRow.updatedAt.getTime()
  const currentTranslationIds = new Set(currentTranslationRows.map(row => row.blockId))
  const retainedTranslationRows = sameTranslationVisit && (changedTranslationSource || currentTranslationRows.length > 0)
    ? [...previousTranslations.rows.filter(row => !currentTranslationIds.has(row.blockId)), ...currentTranslationRows]
    : currentTranslationRows
  if (!sameTranslationVisit || previousTranslations.data !== currentTranslationRows || changedTranslationSource) {
    setPreviousTranslations({ pageId: loadedPageRow.id, language: translationLanguage, version: loadedPageRow.updatedAt.getTime(), data: currentTranslationRows, rows: retainedTranslationRows })
  }
  const translationEligible = effectiveTranslatable(visiblePages.filter(candidate => candidate.spaceId === loadedPageRow.spaceId).map(candidate => ({
    id: candidate.id, translatable: candidate.translatable,
    parentId: placements.find(placement => placement.pageId === candidate.id)?.parentPageId ?? null,
  }))).has(loadedPageRow.id)
  const translations = translationEligible ? retainedTranslationRows : []
  const hasTranslations = translations.some(row => row.text !== null && row.detectedSourceLanguage?.split('-')[0].toLowerCase() !== translationLanguage.split('-')[0].toLowerCase())
  // flashcards-editor-home.decisions.md "Read and edit modes": the learner's choice in this visit, never a history.
  // `view: null` = no choice yet, so the page shows the mode its flag and access open it in.
  const [mode, setMode] = useState<{ pageId: string; view: 'edit' | 'read' | null; text: 'translated' | 'original' }>(() => ({ pageId: loadedPageRow.id, view: null, text: 'translated' }))
  // A snapshot taken on open: the audio is a projection read once, never regenerated by edits.
  const [listening, setListening] = useState<{ pageId: string; translated: boolean; plan: SpeechPlan; session: SpeechSession } | null>(null)
  // Leaving the page closes the player; coming back never resumes an old snapshot.
  if (listening && listening.pageId !== loadedPageRow.id) setListening(null)
  if (mode.pageId !== loadedPageRow.id) setMode({ pageId: loadedPageRow.id, view: null, text: 'translated' })
  const { data: capabilities } = useSuspenseQuery(routerCtx.rpc.page.levels.queryOptions({ workspaceId: resource.space.workspaceId }), routerCtx.queryClient)
  let permissionPageId: string | null = loadedPageRow.id
  const visitedPermissionPages = new Set<string>()
  while (permissionPageId && capabilities.levels[permissionPageId] === undefined && !visitedPermissionPages.has(permissionPageId)) {
    visitedPermissionPages.add(permissionPageId)
    const currentPermissionPageId: string = permissionPageId
    permissionPageId = placements.find(placement => placement.pageId === currentPermissionPageId)?.parentPageId ?? null
  }
  const level = permissionPageId ? capabilities.levels[permissionPageId] ?? 0 : 0
  // No per-page `enabled`: page instances overlap during navigation, and differing values on one shared query flip it during render.
  const generationAvailability = useQuery(routerCtx.rpc.lexicon.canGenerate.queryOptions())
  const canEdit = !accessLost && level >= 2
  const newDatabase = useNewDatabase({ resource, levels: accessLost ? {} : capabilities.levels, beforeCreate: flushPagePatches })
  const canShare = !accessLost && level === 3
  const canView = !accessLost && level >= 1
  // page-views.spec.md: opening a readable page records a view (throttled inside recordPageView).
  useEffect(() => {
    // "Show my view history" off records nothing; read at open time so a change in settings applies to the next open.
    if (canView && routerCtx.auth.getUser().settings.showViewHistory !== false) recordPageView(routerCtx, resource.space.workspaceId, loadedPageRow.id)
  }, [canView, routerCtx, resource.space.workspaceId, loadedPageRow.id])
  // Without write access the page is always read; otherwise the choice, or the mode the Read-only flag opens.
  const view = canEdit ? mode.view ?? (loadedPageRow.readonly ? 'read' : 'edit') : 'read'
  // Translation exists only in read mode, and only while the page has one.
  const pageMode = view === 'edit' ? 'edit' : mode.text === 'translated' && hasTranslations ? 'translated' : 'reader'
  // Keep navigation visible while initial generation holds the authored editor closed.
  const initialTranslations = useQuery({
    queryKey: ['page-initial-translations', resource.userId, resource.space.id, loadedPageRow.id, translationLanguage, translationEligible],
    staleTime: Infinity,
    queryFn: async () => {
      if (translationEligible) await resource.translations?.open(loadedPageRow.id)
      return true
    },
  }, resource.queryClient)
  const translationReady = !translationEligible || initialTranslations.data === true
  // The player reads a snapshot of one view; showing the other text closes it.
  if (listening && listening.translated !== (pageMode === 'translated')) setListening(null)
  const findKey = JSON.stringify([page.workspaceId, loadedPageRow.id, pageMode])
  const [find, setFind] = useState({ key: findKey, open: false })
  if (find.key !== findKey) setFind({ key: findKey, open: false })
  const findOpen = find.key === findKey && find.open
  const [commentComposer, setCommentComposer] = useState({ key: findKey, open: false })
  if (commentComposer.key !== findKey) setCommentComposer({ key: findKey, open: false })
  const commentComposerOpen = commentComposer.key === findKey && commentComposer.open
  const canFind = feEnv().platform === 'web' && errors.reference === undefined && loadedPageRow.deletedAt === null && pageMode !== 'translated' && (page.page.content.kind === 'note' || page.page.content.kind === 'language' || page.page.content.kind === 'databaseRow')
  const canComment = pageMode === 'edit' && errors.reference === undefined && loadedPageRow.deletedAt === null && (page.page.content.kind === 'note' || page.page.content.kind === 'language' || page.page.content.kind === 'databaseRow')
  useEffect(() => {
    return pageMode === 'edit' && translationReady ? resource.translations?.registerEditor(loadedPageRow.id) : undefined
  }, [resource, loadedPageRow.id, pageMode, translationReady])
  const translatedBlocks = applyTranslation(page.blocks, translations)
  // page-translation.decisions.md "Translated title": only the open page's title is translated; other pages keep theirs.
  const translatedTitle = translatedPageTitle(loadedPageRow.title, translations)
  const shownTitle = pageMode === 'translated' ? translatedTitle : page.page.title
  // An adopted entry's editor shows every sense, while its blocks hold only the selected senses' cards.
  const entryPaths = page.page.content.kind === 'lexiconEntry' ? entryMediaPaths(page.page.content.entry) : []
  const media = usePreparedMedia([...new Set([...storedMediaPaths(pageMode === 'translated' ? translatedBlocks : page.blocks), ...entryPaths])].toSorted())
  const translatedKey = JSON.stringify([translatedTitle, translatedBlocks])
  const [translated, setTranslated] = useState(() => ({ key: translatedKey, doc: new Doc({ content: { children: structuredClone(translatedBlocks) as never } }) }))
  if (translated.key !== translatedKey) setTranslated({ key: translatedKey, doc: new Doc({ content: { children: structuredClone(translatedBlocks) as never } }) })
  const practice = usePagePractice({ key: JSON.stringify([page.workspaceId, loadedPageRow.id, pageMode]), cards: pageFlashcards, enabled: pageMode !== 'edit' })
  const editorHandleRef = useRef<BlockEditorHandle | null>(null)
  // Speech highlights paint whichever view the editor shows, translated included.
  const [speechEditor, setSpeechEditor] = useState<BlockEditorHandle | null>(null)
  const { t: tListen } = useTranslation('listen')
  // Voicing is billed per request and the procedure is admin-only.
  const canListen = user.role === 'admin' && (page.page.content.kind === 'databaseRow' || page.page.content.kind === 'note' || page.page.content.kind === 'language')
  async function toggleListening() {
    if (listening) { setListening(null); return }
    const pageId = loadedPageRow.id
    const snapshot = pageMode === 'translated' ? undefined : editorHandleRef.current?.getSnapshot()
    const native = nativeTongueFor(user.settings.lexicon?.nativeLanguage, routerCtx.defaultNativeLanguage)
    const { data: labels } = await loaderData.queryClient.load({
      queryKey: ['speech-labels', native],
      queryFn: () => routerCtx.tel.span({ name: 'speech.labels', input: { native } }, tel => speechLabels(native, async ({ texts, target, format }, callTel) => (await routerCtx.rpcClient.translate.mutate({ texts, target, format }, { tel: callTel })).translations, tel)),
      staleTime: Infinity,
    })
    const plan = planSpeech(narratePage({
      pageId,
      title: pageMode === 'translated' ? translatedTitle : snapshot?.pageTitle ?? loadedPageRow.title,
      blocks: pageMode === 'translated' ? translatedBlocks : (snapshot?.blocks ?? page.blocks),
      pages,
      labels,
    }))
    if (plan.pieces.length === 0) return
    // The chunk plan report (`page-listening.spec.md` → Chunk plan): `String(__speechPlan)`.
    if (isDev) Object.assign(globalThis, { __speechPlan: plan })
    const session = createSpeechSession({
      store: routerCtx.speechStore,
      voice: async ({ text, languageCode }) => {
        const result = await routerCtx.rpcClient.generateTts.mutate({ text, languageCode, voice: 'async', withTimestamps: true })
        return { audio: Uint8Array.fromBase64(result.audioBase64), words: result.wordTimestamps ?? [] }
      },
    })
    setListening({ pageId, translated: pageMode === 'translated', plan, session })
  }
  /** Publish the mode in the initiating event; save pending edits before opening translations. */
  async function showRead(text: 'translated' | 'original') {
    setMode({ pageId: loadedPageRow.id, view: 'read', text })
    try {
      if (view === 'edit') await routerCtx.writes.flush(loadedPageRow.spaceId)
      if (text === 'translated') await resource.translations?.open(loadedPageRow.id)
      report('reader')
    } catch (error) { report('reader', error) }
  }
  function showEdit() {
    setMode({ pageId: loadedPageRow.id, view: 'edit', text: 'translated' })
  }
  // Keep each intent visible until its serialized local commit is observed by the read query.
  const [pendingReadonly, setPendingReadonly] = useState<{ pageId: string; value: boolean; settled: boolean } | null>(null)
  const [pendingTranslatable, setPendingTranslatable] = useState<{ pageId: string; value: boolean; settled: boolean } | null>(null)
  const readonlyQueue = useRef<Promise<unknown>>(Promise.resolve())
  const translatableQueue = useRef<Promise<unknown>>(Promise.resolve())
  const readonly = pendingReadonly?.pageId === loadedPageRow.id ? pendingReadonly.value : loadedPageRow.readonly
  const translatable = pendingTranslatable?.pageId === loadedPageRow.id ? pendingTranslatable.value : loadedPageRow.translatable
  if (pendingReadonly && (pendingReadonly.pageId !== loadedPageRow.id || (pendingReadonly.settled && pendingReadonly.value === loadedPageRow.readonly))) setPendingReadonly(null)
  if (pendingTranslatable && (pendingTranslatable.pageId !== loadedPageRow.id || (pendingTranslatable.settled && pendingTranslatable.value === loadedPageRow.translatable))) setPendingTranslatable(null)

  function setReadonly(value: boolean) {
    const pending = { pageId: loadedPageRow.id, value, settled: false }
    const nextMode = { pageId: loadedPageRow.id, view: value ? 'read' as const : 'edit' as const, text: 'translated' as const }
    setPendingReadonly(pending)
    setMode(nextMode)
    readonlyQueue.current = readonlyQueue.current.then(async () => {
      try {
        if (value) await routerCtx.writes.flush(loadedPageRow.spaceId)
        await commitWorkspacePageEdit(routerCtx, loadedPageRow.spaceId, { pageId: loadedPageRow.id, frame: new Uint8Array(), fields: { readonly: value }, now: new Date() })
        setPendingReadonly(current => current === pending ? { ...pending, settled: true } : current)
        report('readonly')
      } catch (error) {
        setPendingReadonly(current => current === pending ? null : current)
        setMode(current => current === nextMode ? { ...current, view: null } : current)
        report('readonly', error)
        return false
      }
      // Generation failure does not undo a successful local flag commit.
      if (value) {
        try { await resource.translations?.open(loadedPageRow.id) }
        catch (error) { report('readonly', error) }
      }
      return true
    })
  }
  /** The Translatable switch stores the page's own mark; children inherit it (page-translation spec). */
  function setTranslatable(value: boolean) {
    const pending = { pageId: loadedPageRow.id, value, settled: false }
    setPendingTranslatable(pending)
    translatableQueue.current = translatableQueue.current.then(async () => {
      try {
        await commitWorkspacePageEdit(routerCtx, loadedPageRow.spaceId, { pageId: loadedPageRow.id, frame: new Uint8Array(), fields: { translatable: value }, now: new Date() })
        setPendingTranslatable(current => current === pending ? { ...pending, settled: true } : current)
        report('translatable')
      } catch (error) {
        setPendingTranslatable(current => current === pending ? null : current)
        report('translatable', error)
      }
      return true
    })
  }
  const { doc, handle, commits, fileHandler, unload } = documentSession
  const readCommentBlocks = (): Blocks => loadedPageRow.kind === 'note' || loadedPageRow.kind === 'language'
    ? pageDocument(doc).content.children as Blocks : []
  const writes = usePageWrites(doc)
  const liveClient = routerCtx.realtime?.(resource.space.workspaceId) ?? null
  const liveFrames = useLiveEdits(liveClient)
  useEffect(() => {
    if (!liveClient || !handle || pageMode === 'translated') return
    const consumed = []
    try {
      for (const frame of liveFrames) {
        if (frame.pageId !== loadedPageRow.id) { consumed.push(frame); continue }
        const bytes = Uint8Array.fromBase64(frame.update)
        const info = crdt.inspectUpdate(bytes)
        if (info.epoch !== doc.version().epoch) { consumed.push(frame); continue }
        if (info.blocks.some(block => block.user !== frame.userId)) throw new Error('Live frame author mismatch')
        handle.applyRemote(bytes)
        const applied = doc.version()
        writes.advance(doc, receivedLiveVersion(info, applied))
        if (info.blocks.every(block => (applied.vector.get(Number(block.client)) ?? 0) >= block.start + block.length)) consumed.push(frame)
      }
    } catch (error) { reportFromEffect('remoteImport', error) }
    if (consumed.length > 0) liveClient.acknowledgeEdits(consumed)
  }, [liveClient, liveFrames, handle, pageMode, loadedPageRow.id, doc, writes])

  useEffect((): void | (() => void) => {
    if (pageMode === 'translated') return
    // The route read already decoded these bytes; only a remote import needs the document.
    const remote = loadedPageRow.version
    const local = doc.version()
    // A cached subset can be either an own-save echo or an authoritative rewind.
    if (remote.epoch === writes.since().epoch && !containsVersion(remote, writes.since())) {
      let active = true
      void writes.importing((async () => {
        // Root writer ordering settles an in-flight local write before this source read.
        const stored = await resource.db.transaction(tx => tx.query`SELECT ${tx.pages.data} FROM ${tx.pages} WHERE ${tx.pages.id.eq(loadedPageRow.id)}`.executeOne({ data: tx.pages.data }))
        if (!active || !stored || !writes.isCurrent(doc)) return
        const source = crdt.load(stored.data).version()
        if (source.epoch !== writes.since().epoch || containsVersion(source, writes.since())) {
          if (containsVersion(doc.version(), source) || !handle) return
          await routerCtx.writes.flush(loadedPageRow.spaceId)
          if (!active || !writes.isCurrent(doc)) return
          handle.applyRemote(crdt.historyUpdate(stored.data).bytes)
          writes.resync(doc)
          return
        }
        if (!containsVersion(writes.since(), doc.version())) {
          // Its missing predecessor fails the existing drain, which retains the burst.
          await routerCtx.writes.flush(loadedPageRow.spaceId)
          throw new Error('Incomplete update chain')
        }
        // Clean rollback replaces the source; importing it into the ahead Doc cannot undo.
        setDocumentSession(() => createDocumentSession({ id: loadedPageRow.id, spaceId: loadedPageRow.spaceId, data: stored.data }, resource.space.workspaceId, bucket, resource.userId))
      })()).then(() => { if (active) reportFromEffect('remoteImport'); return null }, (error: unknown) => { if (active) reportFromEffect('remoteImport', error) })
      return () => { active = false }
    }
    if (containsVersion(local, remote)) return
    if (!handle) return
    void writes.importing((async () => {
      await routerCtx.writes.flush(loadedPageRow.spaceId)
      if (!writes.isCurrent(doc)) return
      // The stored history as one update; the route read already decoded it, so it is not replayed again here.
      handle.applyRemote(crdt.historyUpdate(loadedPageRow.data).bytes)
      writes.resync(doc)
    })()).then(() => { reportFromEffect('remoteImport'); return null }, (error: unknown) => { reportFromEffect('remoteImport', error) })
  }, [bucket, handle, doc, loadedPageRow.data, loadedPageRow.version, loadedPageRow.id, loadedPageRow.spaceId, pageMode, resource.db, resource.space.workspaceId, resource.userId, routerCtx.writes, writes])

  useEffect(() => {
    async function save(run?: WriteRun, mode?: FlushMode) {
      try {
        // Checkpoint replacement retires a clean session without a stale flush.
        if (!writes.isCurrent(doc)) return
        await commits?.flush(run, mode)
      } catch (error) {
        reportFromEffect('pageEdit', error)
        throw error
      }
    }
    const unregister = routerCtx.writes.register(loadedPageRow.spaceId, save, () => {
      commits?.discard()
      handle?.setEditable(false)
    }, () => { commits?.stop() }, () => { commits?.resume() })
    // The page may be gone before the flush below commits (the worker refuses its work after pagehide): store the
    // pending burst synchronously first; the next open of the workspace commits it (page-unload-journal.ts).
    const leave = () => {
      const pending = commits?.pending() ?? []
      if (pending.length > 0 && writes.isCurrent(doc)) {
        try {
          const frame = doc.encodeUpdate(writes.since()).bytes
          // A burst of only a title has no body to recover (page-unload-journal.ts).
          if (crdt.decode(frame).changes.length > 0) {
            const record = writePageUnload(routerCtx.localStorage, { userId: resource.userId, workspaceId: resource.space.workspaceId }, { pageId: loadedPageRow.id, frame })
            // The new record holds everything this session's earlier records held: retire them once it is stored.
            for (const superseded of unload.splice(0)) routerCtx.localStorage.removeItem(superseded)
            unload.push(record)
          }
        } catch (error) { reportFromEffect('pageEdit', error) }
      }
      void routerCtx.writes.flush(loadedPageRow.spaceId).catch(() => {})
    }
    const stopVisibility = onVisibilityChange(visible => { if (!visible) leave() })
    window.addEventListener('pagehide', leave)
    return () => {
      stopVisibility()
      window.removeEventListener('pagehide', leave)
      // React cannot await cleanup. Keep the save registered until it finishes,
      // so destination loaders and workspace teardown still await this page.
      void writes.afterComments(() => routerCtx.writes.flush(loadedPageRow.spaceId)).then(unregister, () => {})
    }
  }, [commits, doc, handle, loadedPageRow.id, loadedPageRow.spaceId, resource.space.workspaceId, resource.userId, routerCtx.localStorage, routerCtx.writes, unload, writes])

  useEffect(() => {
    if (titleFocusPageIdRef.current !== loadedPageRow.id) return
    const handle = editorHandleRef.current
    if (!handle) return
    if (focusPageTitle(handle)) titleFocusPageIdRef.current = null
  }, [editorHandleRef, loadedPageRow.id])

  const revealQuestion = useEffectEvent((qid: string | undefined, pageId: string) => {
    if (pageId && qid) scroll.scrollTo(qid, { block: 'center', highlight: true })
  })
  useEffect(() => { revealQuestion(qid, page.page.id) }, [qid, page.page.id])

  const createPageRequest = useMutation({
    async mutationFn(input: {
      workspaceId: string
      parentId?: string | null
      teamspaceId?: string
      pageId?: string
      title?: string
      alreadyNavigated?: boolean
      signal?: AbortSignal
    }) {
      await flushPagePatches()
      const pageId = input.pageId ?? ulid()
      await createPageCommand(routerCtx, { workspaceId: input.workspaceId, pageId,
        parentPageId: input.parentId ?? undefined, teamspaceId: input.teamspaceId, title: input.title ?? 'Untitled page',
        blocks: [{ id: blockId(), type: 'p', children: [] }],
      })
      // The destination instance takes title focus from `?focusTitle` (page-writes.model.md).
      if (!input.pageId && !input.signal?.aborted) {
        navigate({ to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
          params: pageRouteParams(routerCtx, input.workspaceId, pageId), search: { focusTitle: pageId } })
      }
      await testSupport?.beforeCreatePage?.(pageId)

      return {
        workspaceId: input.workspaceId,
        pageId,
      }
    },
    onSuccess: (next, input) => {
      if (input.alreadyNavigated || input.signal?.aborted) return
      navigate({
        to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
        params: pageRouteParams(routerCtx, next.workspaceId, next.pageId),
      })
    },
  })

  function handleChange(change: Change) {
    if (!commits) throw new Error('Page has no editable blocks')
    // One rule with the room (live-edit-scope.ts): card, media and page-block effects stay on the device save.
    const live = isRoomPublishable(change.delta)
    if (!live) writes.deviceEdit(change.version)
    if (live && writes.canPublishLive()) {
      try {
        const frame = doc.encodeUpdate(writes.since()).bytes
        if (liveClient?.publishLive({ pageId: loadedPageRow.id, update: frame.toBase64() })) writes.advance(doc, doc.version())
      } catch (error) { report('pageEdit', error) }
    }
    const known = new Set(activePageRef.current.pages.map(row => row.id))
    const created = getAffectedBlocks(change.delta).inserted.page?.find(block => !known.has(block.pageId))
    if (!created) { void commits.push([change]); return null }
    void commits.push([change])
    return track('childPage', routerCtx.writes.flush(loadedPageRow.spaceId).then(() => handleOpenPage(created.pageId)))
  }

  async function editDocument(edit: Parameters<typeof doc.edit>[0], fields?: PageScalarFields, now = new Date(), run?: WriteRun) {
    await routerCtx.writes.flush(loadedPageRow.spaceId)
    await writes.afterImport()
    if (!writes.isCurrent(doc)) throw new Error('Page document session changed')
    const since = doc.version()
    // Stage settings outside the live document: a rejected engine transaction
    // leaves both source and the editor's accepted projection unchanged.
    const staged = crdt.load(doc.encodeSnapshot())
    staged.edit(edit, { user: user.id })
    const frame = staged.encodeUpdate(since).bytes
    const writtenVersion = staged.version()
    await commitWorkspacePageEdit(run ? { ...routerCtx, writes: { ...routerCtx.writes, run } } : routerCtx, page.workspaceId, {
      fields, pageId: page.page.id, frame, userId: user.id, now,
    })
    if (!writes.isCurrent(doc)) return
    // Import through the editor boundary so document and rendered blocks advance
    // together. Input authored during SQL remains in the live document.
    if (handle) handle.applyRemote(frame)
    else doc.applyUpdate(frame)
    writes.advance(doc, writtenVersion)
  }

  const editComments = (change: (blocks: Blocks) => Blocks, refresh: (blocks: Blocks) => void, created?: { id: string; body: string }): Promise<unknown> => {
    return writes.comment(async () => {
      if (!canEdit || pageMode !== 'edit') return null
      await editDocument(tx => {
        const previous = tx.read('content.children') as Blocks
        writeDocument(tx, change(previous), previous)
      })
      if (writes.isCurrent(doc)) refresh(pageDocument(doc).content.children as Blocks)
      return 'saved' as const
    }, task => track('comment', task)).then(result => {
      if (created) resending.current.delete(created.id)
      // A comment that landed despite a failure (after its local commit) is no longer unsent.
      const saved = savedCommentIds(pageDocument(doc).content.children as Blocks)
      const current = unsentRef.current.filter(comment => !saved.has(comment.id))
      if (result === undefined && created) {
        // `track` settles a failed write to undefined; a new comment's text stays on the page with a retry.
        const entry = { ...created, retry: () => {
          if (resending.current.has(created.id)) return
          resending.current.add(created.id)
          void editComments(change, refresh, created)
        } }
        setUnsent(current.some(comment => comment.id === created.id) ? current.map(comment => comment.id === created.id ? entry : comment) : [...current, entry])
        return result
      }
      // A skipped write (null: edit access or mode changed) keeps its row; a saved one drops it.
      const remaining = result === 'saved' && created ? current.filter(comment => comment.id !== created.id) : current
      if (remaining.length !== unsentRef.current.length) setUnsent(remaining)
      // The one 'comment' source is cleared by any comment success; an unsent comment keeps the page unsaved.
      if (result === 'saved' && remaining.length > 0) report('comment', new Error('A comment was not sent'))
      return result
    })
  }

  function addPageComment(selection: BlockReviewSelection, body: string, refresh: (blocks: Blocks) => void) {
    const segment = selection.segments[0]
    if (!segment || selection.segments.length !== 1) return
    const comment = newComment(user.id, body)
    const threadId = crypto.randomUUID()
    void editComments(blocks => upsertCommentThread(blocks, {
      anchorBlockId: segment.blockId,
      anchorText: selection.scope === 'block' ? undefined : selection.text,
      thread: { threadId, status: 'open', comments: [comment] },
    }), refresh, comment)
  }

  function addThreadComment(body: string, refresh: (blocks: Blocks) => void) {
    const comment = newComment(user.id, body)
    const threadId = crypto.randomUUID()
    void editComments(blocks => addPageCommentThread(blocks, { threadId, scope: 'page', status: 'open', comments: [comment] }), refresh, comment)
  }

  function replyComment(threadId: string, body: string, refresh: (blocks: Blocks) => void) {
    const comment = newComment(user.id, body)
    void editComments(blocks => replyCommentThread(blocks, threadId, comment), refresh, comment)
  }

  function handleTitleChange(title: string) {
    if (!canEdit || pageMode !== 'edit') return
    if (title === (draft?.value ?? loadedPageRow.title)) return
    const session = documentSession
    if (!session.commits) throw new Error('Page has no editable blocks')
    const token = {}
    setDraft({ value: title, token })
    void session.commits.push([{ title, version: doc.version() }]).then(result =>
      result === 'discarded' ? 'drop' : loaderData.queryClient.fetchFresh(loaderData.pageOptions.queryKey).then(
        () => { withoutActWarning(() => { report('title') }); return 'drop' },
        (error: unknown) => { withoutActWarning(() => { report('title', error) }); return 'keep' },
      ),
    ).then(result => {
      if (result === 'drop') withoutActWarning(() => { setDraft(current => current?.token === token ? null : current) })
      return null
    })
  }

  const [pendingCards, setPendingCards] = useState<Record<string, { frozen: boolean; deleted: boolean }>>({})

  const [pendingEntry, setPendingEntry] = useState<LexiconEntry | null>(null)
  function handleEntryChange(next: LexiconEntry, current: LexiconEntry) {
    setPendingEntry(next)
    const task = routerCtx.writes.run(() => commitEntryChange(next, current)).finally(() => {
      setPendingEntry(value => value === next ? null : value)
    })
    return writes.entry(task, task => track('entry', task))
  }

  const cardQueue = useRef<Promise<unknown>>(Promise.resolve())
  async function changeCard(qid: string, patch: Partial<{ frozen: boolean; deleted: boolean }>, operation: () => Promise<unknown>) {
    const card = pageFlashcards.find(value => value.qid === qid)
    const previous = pendingCards[qid] ?? { frozen: card !== undefined && card.frozenAt !== null, deleted: card !== undefined && card.deletedAt !== null }
    const pending = { ...previous, ...patch }
    setPendingCards(value => ({ ...value, [qid]: pending }))
    const task = cardQueue.current.catch(() => {}).then(operation)
    cardQueue.current = task
    try { await task } finally {
      setPendingCards(value => {
        if (value[qid] !== pending) return value
        const next = { ...value }
        delete next[qid]
        return next
      })
    }
  }

  async function commitEntryChange(next: LexiconEntry, previousEntry: LexiconEntry) {
    const active = activePageRef.current.page
    if (active.content.kind !== 'lexiconEntry') return
    await editDocument(tx => {
      editEntryQuestions(tx, previousEntry, next, nativeTongueFor(user.settings.lexicon?.nativeLanguage, routerCtx.defaultNativeLanguage), new Set(pageFlashcards.filter(card => card.deletedAt !== null || card.deletedBy !== null).map(card => card.qid)))
    }, { title: next.headword, content: { ...active.content, entry: next } }, new Date(), operation => Promise.resolve().then(operation))
  }

  async function handleDeleteEntry(purge?: PurgeOptions) {
    await flushPagePatches()
    const active = activePageRef.current.page
    if (active.content.kind !== 'lexiconEntry' || !active.parentId) return
    const now = new Date()
    await editDocument(() => {}, { deletedAt: now.getTime() })
    const result = purge
      ? await routerCtx.rpcClient.lexicon.purge.mutate({
          id: active.content.entry.id,
          remote: purge.remote,
        })
      : await routerCtx.rpcClient.lexicon.deleteEntry.mutate({
          id: active.content.entry.id,
        })
    const cleaned = [
      'responsesDeleted' in result &&
      typeof result.responsesDeleted === 'number' &&
      result.responsesDeleted > 0
        ? `${result.responsesDeleted} LLM responses`
        : null,
      result.deleted ? 'shared-dictionary entry' : null,
      'sharedSkippedForAdopters' in result && result.sharedSkippedForAdopters
        ? 'shared entry kept for other adopters'
        : null,
    ].filter((item): item is string => item !== null)
    // Blocked storage (disabled site data) throws: skip the notice, still navigate.
    if (cleaned.length > 0) {
      try {
        localStorage.setItem(
          'flashcards-lexicon-delete-notice',
          `Cleaned: ${cleaned.join(', ')}`,
        )
      } catch { /* no notice */ }
    }
    const destination = active.parentId
    navigate({
      to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
      params: pageRouteParams(routerCtx, active.spaceId, destination),
    })
  }

  async function handleCardFrozen(qid: string, frozen: boolean) {
    const db = stats.db
    await flushPagePatches()
    const active = activePageRef.current.page
    const cards = await db.query`
      SELECT ${cardReadColumns(db.flashcards)} FROM ${learnerCards(user.id)}
      WHERE ${cardVisibleTo(db.flashcards, user.id)}
        AND ${db.flashcards.pageId.eq(active.id)}
        AND ${db.flashcards.qid.eq(qid)}
        AND ${db.flashcards.deletedAt.isNull()}
    `.execute(db.flashcards.__selectSchema)
    await writeWorkspace(routerCtx, resource.space.workspaceId, () => db.transaction(async tx => {
      for (const card of cards) await actOnCard(tx, { userId: user.id, deviceId: routerCtx.deviceId, qid: card.qid, action: { kind: 'frozen', frozenAt: frozen ? Date.now() : null } })
    }, { exclusive: true }))
  }

  async function handleCardDelete(qid: string) {
    await flushPagePatches()
    const active = activePageRef.current.page
    if (active.content.kind !== 'lexiconEntry') return
    await editDocument(tx => {
      const blocks = (pageDocument(doc).content.children as unknown as Block[])
      const index = blocks.findIndex(block => (block.type === 'question' || block.type === 'cloze') && block.qid === qid)
      if (index >= 0) tx.remove('content.children', index, 1)
    })
  }

  async function copyBlockLink(blockId: string) {
    const attempt = ++blockLinkAttempt.current
    const pageId = page.page.id
    const pageKey = JSON.stringify([page.workspaceId, pageId])
    try {
      const params = pageRouteParams(routerCtx, page.workspaceId, pageId)
      const url = new URL(`/${encodeURIComponent(params.workspaceId)}/spaces/${encodeURIComponent(params.spaceId)}/pages/${encodeURIComponent(pageId)}`, window.location.origin)
      url.searchParams.set('blockId', blockId)
      await navigator.clipboard.writeText(url.href)
      if (blockLinkAttempt.current === attempt) report(`blockLink:${pageKey}`)
    } catch (error) {
      if (blockLinkAttempt.current === attempt) report(`blockLink:${pageKey}`, error)
    }
  }

  function handleOpenPage(pageId: string, requestedSpaceId?: string) {
    return _openPage({ routerCtx, navigate, report, flush: flushPagePatches, workspaceId: resource.space.workspaceId }, pageId, requestedSpaceId ?? page.workspaceId)
  }

  async function searchWorkspacePages(query: string, onUpdate?: Parameters<SearchPages>[1]) {
    // Commit the buffered mention locally so engine search includes its source.
    await flushPagePatches()
    const resources = contentResources(routerCtx.spaces.snapshot(resource.space.workspaceId))
    // Search is eventually consistent; this one-shot read drains marked pages first, like searchPages.
    await Promise.all(resources.map(value => refreshPageSearch(value.db)))
    async function run(prepared: Pick<Awaited<ReturnType<typeof prepareSpaceSearch>>, 'options'>, tel: Tel) {
      const results = await Promise.all(resources.map(value =>
        value.queryClient.fetchQuery(prepared.options(value), { tel }),
      ))
      return mergeSearchHits(results.flat(), query, 20).map(hit => ({ spaceId: hit.spaceId, id: hit.pageId, title: hit.title }))
    }
    // One user-operation trace owns lexical DB search and query embedding,
    // including the completed lexical subtree when the embedding worker fails.
    return routerCtx.tel.span({ name: 'page.search', target: 'job', input: { query } }, async (spanTel) => {
      // Publish lexical hits before model download or inference completes.
      const lexical = await spanTel.span({ name: 'page.search.lexical', target: 'job', input: { query } }, async lexicalTel => {
        const search = await prepareSpaceSearch({ query, mode: 'fulltext', limit: 20, embeddings: routerCtx.searchEmbeddings, tel: lexicalTel })
        return run(search, lexicalTel)
      })
      onUpdate?.(lexical)
      const search = await prepareSpaceSearch({ query, limit: 20, embeddings: routerCtx.searchEmbeddings, tel: spanTel })
      const local = await run(search, spanTel)
      // The rerank continues after `page.search` has ended, so it opens its
      // own span instead of nesting late work under an already-ended trace.
      void Promise.resolve(search.reranked).then(reranked => reranked
        ? routerCtx.tel.span({ name: 'page.search.rerank', target: 'job', input: { query } }, rerankTel => run(reranked, rerankTel))
        : undefined)
        .then(hits => hits && onUpdate?.(hits))
        .catch(error => { routerCtx.tel.warn('Page search rerank unavailable; local results retained.', { error }) })
      return local
    })
  }

  function flushPagePatches() {
    return _flushPage(writes, routerCtx, loadedPageRow.spaceId)
  }

  // page-views.spec.md "Showing views": a workspace with no other member has no page anyone else can access, so it sends no views query.
  const othersInWorkspace = [...commentAuthors.keys()].some(memberId => memberId !== user.id)
  const views = usePageViews({ workspaceId: resource.space.workspaceId, pageId: loadedPageRow.id, enabled: canEdit && othersInWorkspace })
  const trashPage = useMutation({
    mutationFn: (input: { pageId: string; spaceId: string }) => _trashPage({ routerCtx, navigate, flush: flushPagePatches, openPage: handleOpenPage, tree: pageTree, workspaceId: resource.space.workspaceId, open: true }, input),
  })
  // Only editor-rendered pages have read and edit modes.
  const hasModes = page.page.content.kind === 'databaseRow' || page.page.content.kind === 'note' || page.page.content.kind === 'language'
  const canTrash = canEdit && loadedPageRow.deletedAt === null && loadedPageRow.parentId !== null && loadedPageRow.id !== resource.space.personalRootId

  // Path like Notion: readable ancestors on the child-edge chain the sidebar reads; root pages are never crumbs.
  const ancestors: PageMeta[] = []
  const visitedAncestors = new Set([loadedPageRow.id])
  let ancestorId = placements.find(placement => placement.pageId === loadedPageRow.id)?.parentPageId ?? null
  while (ancestorId && ancestorId !== resource.space.personalRootId && !visitedAncestors.has(ancestorId)) {
    visitedAncestors.add(ancestorId)
    const currentAncestorId: string = ancestorId
    const ancestor = visiblePages.find(candidate => candidate.id === currentAncestorId && candidate.spaceId === loadedPageRow.spaceId)
    if (ancestor) ancestors.unshift(ancestor)
    ancestorId = placements.find(placement => placement.pageId === currentAncestorId)?.parentPageId ?? null
  }
  const hiddenAncestors = ancestors.length > 2 ? ancestors.slice(1, -1) : []
  const shownAncestors = ancestors.length > 2 ? [ancestors[0], null, ancestors.at(-1)] : ancestors

  // Translated is a mode of read mode: the control exists only there and swaps the text (spec "Read and edit modes").
  function toggleTranslation() {
    void showRead(pageMode === 'translated' ? 'original' : 'translated')
  }
  const translationControl = hasTranslations ? isMobile ? (
    <MenuCheckboxItem checked={pageMode === 'translated'} data-testid="flashcards-page-translation-mode" onCheckedChange={toggleTranslation}>
      <LanguagesIcon />{t('translatedMode')}
    </MenuCheckboxItem>
  ) : (
    // Two buttons swapped by text: each label names its action and never changes with state (WAI-ARIA APG, button).
    pageMode === 'translated' ? (
      <IconButton key="show-original" aria-label={t('showOriginal')} className="size-8 rounded-md" data-testid="flashcards-page-translation-mode" semanticRole="secondary" size="sm" type="button" variant="text" onClick={toggleTranslation}>
        <FileTextIcon />{t('showOriginal')}
      </IconButton>
    ) : (
      <IconButton key="translate" aria-label={t('translate')} className="size-8 rounded-md" data-testid="flashcards-page-translation-mode" semanticRole="secondary" size="sm" type="button" variant="text" onClick={toggleTranslation}>
        <LanguagesIcon />{t('translate')}
      </IconButton>
    )
  ) : null
  const listenControl = canListen ? isMobile ? (
    <MenuCheckboxItem checked={listening !== null} data-testid="flashcards-page-listen" onCheckedChange={() => { void toggleListening() }}>
      <HeadphonesIcon />{tListen('listen')}
    </MenuCheckboxItem>
  ) : (
    <IconButton aria-label={tListen('listen')} aria-pressed={listening !== null} className="size-8 rounded-md" data-testid="flashcards-page-listen" semanticRole="secondary" size="sm" type="button" variant="text" onClick={() => { void toggleListening() }}>
      <HeadphonesIcon />{tListen('listen')}
    </IconButton>
  ) : null
  // Past viewers only for readers who may see views (Edit+ or admin); live collaborators follow presence access.
  const people = <PagePeople pageId={loadedPageRow.id} ownUserId={user.id} views={canEdit ? views : undefined} />

  const navigationContent = <PageNavigation workspaceMenu={workspaceMenu} resource={resource} resources={resources} workspaces={page.workspaces}
    tree={pageTree} pageId={page.page.id} pageSpaceId={page.page.spaceId} parentId={readableParentId} workspaceId={page.workspaceId}
    teamspaces={teamspaces} dueCount={dueCount} invitationCount={workspaceDirectory.invites.length} userId={user.id}
    pending={createPageRequest.isPending} createPage={createPageRequest.mutateAsync} errors={errors} saveError={saveError} report={report} track={track}
    writes={writes} beginWorkspaceSelection={beginWorkspaceSelection} editorHandleRef={editorHandleRef} />

  if (isMobile && nav === '1') {
    return (
      <main
        
        className="bg-surface-1 text-on-surface flex h-dvh w-full flex-col overflow-hidden py-4 [&>[data-sidebar]]:px-4"
        data-testid="flashcards-mobile-navigation"
      >
        {navigationContent}
      </main>
    )
  }

  return (
    <ImageLoadingProvider pending={media.pending} onRetry={media.retry}>
    <div className="contents"  data-testid="flashcards-authoring-surface">
      {isMobile ? null : (
        <Sidebar collapsible="offcanvas">
          {navigationContent}
          <SidebarRail />
        </Sidebar>
      )}

      <SidebarInset className="bg-surface-1 min-w-0">
        {errors.reference !== undefined || loadedPageRow.deletedAt !== null ? <p role={errors.reference === undefined ? undefined : 'alert'}>Page unavailable</p> : null}
        <header
          className={`bg-surface-1 sticky top-0 z-20 min-h-12 shrink-0 p-4 transition-shadow${
            isAtTop ? '' : ' shadow-sm'
          }`}
          data-testid="flashcards-page-header"
          ref={headerRef}
        >
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {isMobile ? (
                <IconButton
                  aria-label={t('backToParent')}
                  data-testid="flashcards-mobile-pages-back"
                  semanticRole="secondary"
                  size="sm"
                  type="button"
                  variant="text"
                  onClick={() => {
                    if (dismissVocabularyPreview.current) {
                      dismissVocabularyPreview.current()
                      return
                    }
                    const vocabularyPageId = page.page.content.kind === 'lexiconEntry'
                      ? readableParentId
                      : null
                    navigate({
                      to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
                      params: pageRouteParams(routerCtx, page.workspaceId, vocabularyPageId ?? page.page.id),
                      search: vocabularyPageId ? {} : { nav: '1' },
                    })
                  }}
                >
                  <ChevronLeftIcon />
                  {t('backToParent')}
                </IconButton>
              ) : largeFinePointer ? null : (
                <SidebarTrigger
                  aria-label="Toggle navigation"
                  data-testid="flashcards-sidebar-trigger"
                  semanticRole="secondary"
                  size="sm"
                  type="button"
                  variant="text"
                />
              )}
              <span className="sr-only">{resource.space.kind === 'private' ? 'Only you can access this page' : 'Everyone in this space can edit'}</span>
              <nav aria-label={t('breadcrumb')} className="flex min-w-0 items-center gap-1" data-testid="flashcards-page-breadcrumb">
                {isMobile ? null : shownAncestors.map(ancestor => <Fragment key={ancestor?.id ?? 'hidden'}>
                  {ancestor ? (
                    <Button className="min-w-0 max-w-40 shrink px-1" data-testid={`flashcards-page-breadcrumb-${ancestor.id}`} semanticRole="secondary" size="sm" title={_getPageTitle(ancestor)} type="button" variant="text"
                      onClick={() => { void handleOpenPage(ancestor.id, ancestor.spaceId) }}>
                      <span className="block min-w-0 truncate" data-crumb="">{_getPageTitle(ancestor)}</span>
                    </Button>
                  ) : (
                    <span className="text-on-surface-secondary shrink-0 text-sm" data-crumb="" data-testid="flashcards-page-breadcrumb-ellipsis" title={hiddenAncestors.map(hidden => _getPageTitle(hidden)).join(' / ')}>…</span>
                  )}
                  <span aria-hidden="true" className="text-on-surface-secondary shrink-0 text-sm">/</span>
                </Fragment>)}
                <span
                  aria-current="page"
                  className="text-on-surface-secondary block min-w-0 truncate text-sm"
                  data-crumb=""
                  data-testid="flashcards-page-header-title"
                  title={_getPageTitle(page.page)}
                >
                  {_getPageTitle(page.page)}
                </span>
              </nav>
            </div>
            {errors[`blockLink:${JSON.stringify([page.workspaceId, page.page.id])}`] === undefined ? null : <p role="alert">Could not copy block link</p>}
            {hasModes ? (
              view === 'read' && canEdit ? (
                <IconButton
                  aria-label={t('editMode')}
                  className="size-8 rounded-md"
                  data-testid="flashcards-page-edit-mode"
                  semanticRole="secondary"
                  size="sm"
                  type="button"
                  variant="text"
                  onClick={showEdit}
                >
                  <Edit2Icon />
                  {t('editMode')}
                </IconButton>
              ) : null
            ) : null}
            {isMobile ? null : <>{translationControl}{listenControl}</>}
            {canFind ? <PageFind
              key={JSON.stringify([page.workspaceId, page.page.id, pageMode])}
              open={findOpen}
              onOpenChange={open => { setFind({ key: findKey, open }) }}
              readBlocks={() => pageDocument(doc).content.children as unknown as Blocks}
              onReveal={blockId => { editorHandleRef.current?.revealBlock(blockId) }}
            /> : null}
            {people}
            {/* One page-actions menu on every viewport, like Notion's •••; mobile also folds the header controls into it. */}
            <Menu>
              <MenuTrigger render={<IconButton aria-label={t('moreActions')} className={isMobile ? undefined : 'size-8 rounded-md'} data-testid="flashcards-page-more" semanticRole="secondary" size="sm" variant="text" />}>
                <MoreHorizontalIcon />{t('moreActions')}
              </MenuTrigger>
              <MenuPopup align="end" finalFocus={findOpen || commentComposerOpen ? false : undefined}>
                {isMobile ? translationControl : null}
                {isMobile ? listenControl : null}
                {canFind ? <MenuItem data-testid="flashcards-find-note" onClick={() => { setFind({ key: findKey, open: true }) }}><SearchIcon />{t('findInNote')}</MenuItem> : null}
                {isMobile && canComment ? <MenuItem data-testid="flashcards-page-comment" onClick={() => { setCommentComposer({ key: findKey, open: true }) }}><MessageSquareIcon />{t('comment')}</MenuItem> : null}
                {canEdit && hasModes ? <MenuCheckboxItem checked={readonly} variant="switch" data-testid="flashcards-page-readonly" onCheckedChange={checked => { setReadonly(checked) }}><LockIcon />{t('readOnly')}</MenuCheckboxItem> : null}
                {canEdit ? <MenuCheckboxItem checked={translatable} variant="switch" data-testid="flashcards-page-translatable" onCheckedChange={checked => { setTranslatable(checked) }}><LanguagesIcon />{t('translatable')}</MenuCheckboxItem> : null}
                {canShare ? <MenuItem data-testid="flashcards-share-page" onClick={onShare}><Share2Icon />{t('share')}</MenuItem> : null}
                {canTrash ? <MenuItem className="data-highlighted:bg-error/8 data-highlighted:text-error" data-testid="flashcards-page-more-trash" disabled={trashPage.isPending} onClick={() => { trashPage.mutate({ pageId: loadedPageRow.id, spaceId: loadedPageRow.spaceId }) }}><Trash2Icon />{t('trashPage')}</MenuItem> : null}
              </MenuPopup>
            </Menu>
            {trashPage.error ? <div role="alert"><p>{t('unableToTrashPage')}</p><ErrorDetails error={unknownErrorToError(trashPage.error)} compact /></div> : null}
            <PagePresence pageId={loadedPageRow.id} editor={pageMode === 'translated' ? null : speechEditor} ownUserId={user.id} />
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col items-center">
          {createPageRequest.error ? <p role="alert">Unable to create page</p> : null}
          <WorkspaceSyncAlerts workspaceId={resource.space.workspaceId} className="max-w-[700px] px-6" />
          {newDatabase.dialog}
          {loadedPageRow.deletedAt === null ? <section
            onKeyDownCapture={event => {
              if (event.key === 'Enter' && event.target instanceof HTMLElement && event.target.dataset.testid === 'content-blocks-editor-page-title') {
                void track('flush', flushPagePatches())
              }
            }}
            ref={section => {
              // Pointer-only refinement of the title's select-on-open (keyboard focus already selects it), so a native listener, not a static-element onClick.
              const onClick = (event: MouseEvent) => { if (canEdit && pageMode === 'edit' && event.target) selectUntouchedTitleOnClick(editorHandleRef.current, event.target) }
              section?.addEventListener('click', onClick)
              return () => { section?.removeEventListener('click', onClick) }
            }}
            onBlur={event => {
              const titleBlur = event.target instanceof HTMLElement && event.target.dataset.testid === 'content-blocks-editor-page-title'
              if (!titleBlur && event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return
              void track('flush', flushPagePatches())
            }}
            className={`min-h-[60vh] w-full ${pageMode === 'edit' && (page.page.content.kind === 'note' || page.page.content.kind === 'language') ? 'max-w-[1044px]' : 'max-w-[700px]'}`}
          >
            {page.page.content.kind === 'vocabulary' && loaderData.parentLanguageCode ? (
              <VocabularyPage
                key={`${page.page.id}:${q ?? ''}`}
                dismissPreviewRef={dismissVocabularyPreview}
                pageKind={page.page.content.kind}
                language={loaderData.parentLanguageCode}
                user={user}
                workspaceId={page.workspaceId}
                canWrite={canEdit}
                generationAvailability={generationAvailability}
              />
            ) : page.page.content.kind === 'lexiconEntry' ? (
              <>
              <AdoptBumpError pageId={page.page.id} />
              <EntryEditor
                spaceId={page.workspaceId}
                key={page.page.id}
                mode="adopted"
                generationRefused={generationAvailability.data?.canGenerate !== true}
                readOnly={!canEdit}
                deviceId={routerCtx.deviceId}
                entry={pendingEntry ?? page.page.content.entry}
                l1={nativeTongueFor(
                  user.settings.lexicon?.nativeLanguage,
                  routerCtx.defaultNativeLanguage,
                )}
                rulePageExists={
                  page.page.content.entry.kind === 'lexeme' &&
                  _findRulePageId(
                    page.pages,
                    readableParentId,
                    page.page.content.entry.inflectionClass,
                    rulePages ?? [],
                  ) !== undefined
                }
                onOpenRulePage={() => {
                  if (page.page.content.kind !== 'lexiconEntry') return
                  if (page.page.content.entry.kind !== 'lexeme') return
                  const rulePageId = _findRulePageId(
                    page.pages,
                    readableParentId,
                    page.page.content.entry.inflectionClass,
                    rulePages ?? [],
                  )
                  if (rulePageId) void handleOpenPage(rulePageId)
                }}
                onChange={(next, current) => {
                  void handleEntryChange(next, current)
                }}
                onDelete={canEdit ? purge => track('entryDelete', handleDeleteEntry(purge)) : undefined}
                cardStates={{ ...Object.fromEntries(
                  pageFlashcards.map((card) => [
                    card.qid,
                    {
                      frozen: card.frozenAt !== null,
                      deleted: card.deletedAt !== null,
                    },
                  ]),
                ), ...pendingCards }}
                onSetCardFrozen={canEdit ? (qid, frozen) => track('cardFrozen', changeCard(qid, { frozen }, () => handleCardFrozen(qid, frozen))) : undefined}
                onDeleteCard={canEdit ? qid => track('cardDelete', changeCard(qid, { deleted: true }, () => handleCardDelete(qid))) : undefined}
                onRequestCard={canEdit ? qid => (async () => {
                  if (page.page.content.kind !== 'lexiconEntry' || page.page.content.entry.kind !== 'lexeme') return
                  const cell = Object.entries(page.page.content.entry.paradigm).find(([, form]) => form.formRecallQuestionId === qid)?.[0]
                  if (!cell) return
                  await track('cardRequest', changeCard(qid, { frozen: false, deleted: false }, () => writeWorkspace(routerCtx, page.workspaceId, () => requestPredictedCell(routerCtx.db(page.workspaceId), { userId: user.id, pageId: page.page.id, cell, translationLanguage: nativeTongueFor(user.settings.lexicon?.nativeLanguage, routerCtx.defaultNativeLanguage) }))))
                })() : undefined}
                resolveMediaSrc={media.resolve}
              />
              </>
            ) : null}
            {pageMode === 'edit' && !translationReady && (page.page.content.kind === 'databaseRow' || page.page.content.kind === 'note' || page.page.content.kind === 'language') ? <h1 data-testid="content-blocks-editor-page-title" aria-busy="true" className="px-6 py-4 text-4xl font-bold">{shownTitle}</h1> : null}
            {page.page.content.kind === 'databaseRow' || page.page.content.kind === 'note' ||
            page.page.content.kind === 'language' ? (pageMode !== 'edit' || translationReady) && (
              <PageComments doc={doc} enabled={pageMode === 'edit'} read={readCommentBlocks} unsent={unsentComments}>{({ blocks, onStateChange, refresh }) => (
                <BlockEditor
                  linkedBlockId={linkedBlockId}
                  onCopyBlockLink={blockId => { void copyBlockLink(blockId) }}
                  key={JSON.stringify([page.workspaceId, page.page.id])}
                  spaceId={page.workspaceId}
                  doc={pageMode === 'translated' ? translated.doc : doc}
                  pageTitle={shownTitle}
                  onPageTitleChange={handleTitleChange}
                  mode={pageMode === 'edit' ? 'edit' : 'answer'}
                  answerPractice={practice.answerPractice}
                  onStateChange={onStateChange}
                  comments={pageMode === 'edit' ? [...new Map(getBlockCommentThreads(blocks)
                    .map(({ anchor, ref, thread }) => [thread.threadId, { ...thread, anchorBlockId: anchor.id, selectedText: commentAnchorText(ref) }])).values()] : undefined}
                  pageComments={pageMode === 'edit' ? {
                    composer: isMobile ? { open: commentComposerOpen, onOpenChange: open => { setCommentComposer({ key: findKey, open }) } } : undefined,
                    threads: getPageCommentThreads(blocks),
                    currentAuthor: currentCommentAuthor,
                    resolveAuthor: resolveCommentAuthor,
                    onAdd: body => { addThreadComment(body, refresh) },
                    onReply: (threadId, body) => { replyComment(threadId, body, refresh) },
                    onEdit: (threadId, messageId, body) => { void editComments(blocks => editCommentMessage(blocks, threadId, messageId, body), refresh) },
                    onDelete: (threadId, messageId) => { void editComments(blocks => removeCommentMessage(blocks, threadId, messageId), refresh) },
                    onResolve: threadId => { void editComments(blocks => acceptCommentThread(blocks, threadId), refresh) },
                    onReopen: threadId => { void editComments(blocks => reopenCommentThread(blocks, threadId), refresh) },
                  } : undefined}
                  review={pageMode === 'edit' ? {
                    sourceMap: commentSourceMap(blocks),
                    currentCommentAuthor,
                    resolveCommentAuthor,
                    onAddComment: (selection, body) => { addPageComment(selection, body, refresh) },
                    onReplyComment: (threadId, body) => { replyComment(threadId, body, refresh) },
                    onEditComment: (threadId, messageId, body) => { void editComments(blocks => editCommentMessage(blocks, threadId, messageId, body), refresh) },
                    onDeleteComment: (threadId, messageId) => { void editComments(blocks => removeCommentMessage(blocks, threadId, messageId), refresh) },
                    onAcceptComment: threadId => { void editComments(blocks => removeCommentThread(blocks, threadId), refresh) },
                  } : undefined}
                  onChange={(change, clipboard) => {
                    if (!canEdit || pageMode !== 'edit') return
                    void (() => {
                      testSupport?.recordChange?.(change)
                      return handleChange(change)
                    })()
                    if (clipboard?.cut) void track('flush', routerCtx.writes.flush(loadedPageRow.spaceId))
                  }}
                  registerQuestionTarget={scroll.register}
                  renderQuestionSettings={pageMode === 'edit' ? qid => <QuestionCurrentSettings qid={qid} cards={pageFlashcards} resource={resource} /> : undefined}
                  onReady={(handle) => {
                    setSpeechEditor(handle)
                    practice.attach(handle)
                    if (pageMode === 'translated') return
                    editorHandleRef.current = handle
                    setDocumentSession(current => {
                      if (current.handle === handle) return current
                      const commits = createPageCommitBuffer<PageBufferedEdit>({
                        localCommitBoundary: true,
                        policy: routerCtx.pageCommit,
                        requestFlush: () => routerCtx.writes.flush(loadedPageRow.spaceId, 'due'),
                        async commit(changes, run, locallyCommitted) {
                          const last = changes.at(-1)
                          if (!last) return
                          const titleEdit = changes.findLast((change): change is PageTitleEdit => 'title' in change)
                          if (!writes.isCurrent(current.doc)) throw new Error('Page document session changed')
                          await liveClient?.settleLive(loadedPageRow.id, current.doc.version().epoch)
                          const ctx = run ? { ...routerCtx, writes: { ...routerCtx.writes, run } } : routerCtx
                          const journaled = [...current.unload]
                          const frame = current.doc.encodeUpdate(writes.since())
                          // encodeUpdate sealed the open text tail, so this synchronous read is a complete-change end
                          // (crdt spec 11 cell 37); `last.version` can end inside the tail and reject `Partial transaction`.
                          const written = current.doc.version()
                          const advance = () => {
                            writes.advance(current.doc, written)
                            // This frame holds everything the records written before it held; later records stay.
                            for (const record of journaled) {
                              routerCtx.localStorage.removeItem(record)
                              const index = current.unload.indexOf(record)
                              if (index >= 0) current.unload.splice(index, 1)
                            }
                            locallyCommitted?.()
                          }
                          // Keystrokes already sealed into the previous frame leave nothing to write (crdt spec 11 cell 52).
                          if (!titleEdit && crdt.decode(frame.bytes).changes.length === 0) {
                            advance()
                            report('pageEdit')
                            return
                          }
                          const parent = { pageId: loadedPageRow.id, userId: user.id, now: new Date(),
                            fields: titleEdit ? { title: titleEdit.title } : undefined,
                            frame: frame.bytes, tel: routerCtx.tel, bucket,
                            ...(changes.every(change => 'textOnly' in change && change.textOnly) ? { prose: true as const } : {}) }
                          // Applied page-block insertions create absent children inside this transaction.
                          // The buffer releases the next SQL frame locally, but barriers join reader acknowledgement.
                          await writeWorkspace(ctx, loadedPageRow.spaceId, async () => {
                            await commitPageEdits(resource.db, [parent])
                            advance()
                          })
                          report('pageEdit')
                        },
                        onError: error => { report('pageEdit', error) },
                      })
                      return { ...current, handle, commits }
                    })
                    if (titleFocusPageIdRef.current === page.page.id && focusPageTitle(handle)) {
                      titleFocusPageIdRef.current = null
                    }
                    testSupport?.registerEditor?.({
                      pageId: page.page.id,
                      editor: handle, doc, initialSnapshot: documentSession.initialSnapshot,
                    })
                  }}
                  fileHandler={{ ...fileHandler, getUrl: media.resolve }}
                  onOpenPage={(ref) => {
                    void handleOpenPage(ref.pageId, ref.spaceId)
                  }}
                  resolvePage={(ref) => _resolvePage(page.pages, ref)}
                  searchPages={searchWorkspacePages}
                  slashMenuItems={newDatabase.item ? [newDatabase.item] : undefined}
                  className="text-on-surface text-body-medium mx-auto w-full max-w-[700px] min-w-0 overflow-x-clip px-6 pb-8 leading-relaxed break-words outline-none"
                  placeholder={null}
                />
              )}</PageComments>
            ) : null}
            {backlinks.length > 0 ? (
              <section
                className="mt-12 border-border border-t px-6 pt-6 pb-10"
                data-testid="flashcards-page-backlinks"
              >
                <h2 className="text-on-surface-secondary text-sm font-medium">
                  {t('backlinks')}
                </h2>
                <ul className="mt-3 flex flex-col gap-1">
                  {backlinks.map((backlink) => (
                    <li key={backlink.pageId}>
                      <button
                        className="text-on-surface hover:text-primary w-full truncate text-left text-sm underline-offset-4 hover:underline"
                        data-testid={`flashcards-page-backlink-${backlink.pageId}`}
                        type="button"
                        onClick={() => {
                          void handleOpenPage(backlink.pageId, backlink.spaceId)
                        }}
                      >
                        {backlink.title.trim() || t('untitledPage')}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </section> : null}
          {listening ? (
            <PageListenBar
              editor={speechEditor}
              plan={listening.plan}
              session={listening.session}
              onClose={() => { setListening(null) }}
            />
          ) : null}
        </div>
      </SidebarInset>
    </div>
    </ImageLoadingProvider>
  )
}


function VocabularyPage({
  dismissPreviewRef,
  pageKind,
  language,
  user,
  workspaceId: spaceId,
  canWrite,
  generationAvailability,
}: {
  dismissPreviewRef: React.RefObject<(() => void) | null>
  canWrite: boolean
  // One observer (AvailablePageContent) owns canGenerate; a second with a different `enabled` re-enables it during render.
  generationAvailability: UseQueryResult<{ canGenerate: boolean }>
  language: 'en' | 'de' | 'ru'
  pageKind: 'vocabulary' | 'language'
  user: FlashcardsUser
  workspaceId: string
}) {
  // Preview purge stays on the page, so it cannot use the navigate-away
  // localStorage notice below — it reports inline instead.
  const [pendingAdoption, setPendingAdoption] = useState<LexiconEntry | null>(null)
  const [purgeError, setPurgeError] = useState<unknown>(null)
  // A failed dictionary lookup reports inline.
  const [openError, setOpenError] = useState<unknown>(null)
  const [purgeNotice, setPurgeNotice] = useState<string | null>(null)
  const [deleteNotice] = useState(() => {
    // Blocked storage (Safari private mode, disabled site data) throws: show no notice.
    try {
      const notice = localStorage.getItem('flashcards-lexicon-delete-notice')
      localStorage.removeItem('flashcards-lexicon-delete-notice')
      return notice
    } catch { return null }
  })
  const loaderData = usePageView()
  const routerCtx = useRouterContext()
  const isMobile = useIsMobile()
  // Browser connectivity as React state, updated by the online/offline events.
  const [online, setOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const update = () => { setOnline(navigator.onLine) }
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])
  const canGenerate = generationAvailability.data?.canGenerate === true && online
  const nativeLanguage = user.settings.lexicon?.nativeLanguage
  const navigate = useNavigate()
  const { preview: previewEntryId, q } = useSearch({
    from: '/$workspaceId/spaces/$spaceId/pages/$pageId',
  })
  const { pageId: routePageId } = useParams({
    from: '/$workspaceId/spaces/$spaceId/pages/$pageId',
  })
  const { t } = useTranslation('page')
  const [text, setText] = useState(q ?? '')
  const [searchOpen, setSearchOpen] = useState(Boolean(q))
  const [wordToGenerate, setWordToGenerate] = useState<string | null>(null)
  // Remote search is the expensive leg (network round trip), so it's debounced;
  // the local trigram lookup below stays keyed on `text` directly and fires on
  // every keystroke since it's cheap (single indexed query, no N+1).
  // Shared hook owns timer-driven React notification and its test act boundary.
  const debouncedText = useDebounce(text, LEXICON_REMOTE_SEARCH_DEBOUNCE_MS)
  const [preview, setPreview] = useState<CachedEntry | null>(null)
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(
    previewEntryId ?? null,
  )
  const [generationError, setGenerationError] = useState<unknown>(null)
  // True while the preview is an unfinished or failed generation: it is not indexed yet, so it cannot be added.
  const [previewIsPartial, setPreviewIsPartial] = useState(false)
  const generationKey = text.trim().toLocaleLowerCase()
  const [rowHighlighted, setRowHighlighted] = useState(false)
  const adoptedOptions = loaderData.entryPagesOptions
  const childrenOptions = loaderData.languageChildrenOptions
  if (!adoptedOptions || !childrenOptions) {
    throw new Error('Vocabulary page data was not loaded')
  }
  const { data: adoptedPages } = useSuspenseQuery(adoptedOptions, loaderData.queryClient)
  const adopted = new Map(adoptedPages.map(row => [row.entryId, row.pageId]))
  const { data: children } = useSuspenseQuery(childrenOptions, loaderData.queryClient)
  const { data: rulePages } = useSuspenseQuery(loaderData.rulePagesOptions, loaderData.queryClient)
  // SQL ranks/limits the engine postings before decoding displayed hits.
  const localDb = routerCtx.db(spaceId)
  const localSearch = useQuery({
    ...dbQueryOptions(lexiconPageSearchQuery(localDb, { language, text }),
      s.transform(s.unknown(), value => lexiconPageHit(localDb.pages.__selectSchema.parse(value), text))),
    enabled: lexiconTrigrams(text).length > 0,
  }, loaderData.queryClient)
  const localHits = localSearch.data ?? []
  // Remote search: keyed on the debounced text, so typing never fires more than one
  // in-flight remote round trip. React Query cancels/ignores a superseded fetch via
  // the AbortSignal and by binding results to their own query key, so a slow earlier
  // response can never land on top of a newer one. Remote search still discovers
  // homographs when an adopted local entry already matches exactly.
  const remoteSearchOptions = routerCtx.rpc.lexicon.search.queryOptions({
    language,
    text: debouncedText,
  })
  const remoteSearch = useQuery({
    ...remoteSearchOptions,
    // Offline the dictionary server is not asked (lexicon table cells A.5, B.4); adopted page hits still show.
    enabled: lexiconTrigrams(debouncedText).length > 0 && online,
  })
  const remoteHits = (online ? remoteSearch.data ?? [] : []).map((hit) => {
    const pageId = adopted.get(hit.entryId)
    return pageId ? Object.assign({}, hit, { origin: 'pages' as const, pageId }) : Object.assign({}, hit, { origin: 'cache' as const })
  })
  const hits = rankHits(text, [...localHits, ...remoteHits])
  const selectedEntry = useQuery({
    ...routerCtx.rpc.lexicon.getEntry.queryOptions({
      id: selectedEntryId ?? '',
    }),
    enabled: selectedEntryId !== null,
  })
  const generationLifetime = useRef<AbortController | null>(null)
  useEffect(() => {
    const lifetime = new AbortController()
    generationLifetime.current = lifetime
    return () => { lifetime.abort() }
  }, [])
  const generate = useMutation({
    mutationFn: (input: { text: string }) => Promise.resolve((async () => {
      const lifetime = generationLifetime.current
      if (!lifetime || lifetime.signal.aborted) return null
      const stream = routerCtx.rpcClient.lexicon.generateStream.subscribe({ language, text: input.text })
      const unsubscribe = () => { stream.unsubscribe() }
      lifetime.signal.addEventListener('abort', unsubscribe, { once: true })
      try {
        for await (const event of stream) {
          if (event.type === 'error') throw new Error(event.message)
          if (event.type === 'done') {
            if (lifetime.signal.aborted) return null
            withoutActWarning(() => {
              setPreview(event.entry)
              setPreviewIsPartial(false)
            })
            return event.entry
          }
          const { entry } = event
          if (lifetime.signal.aborted) return null
          withoutActWarning(() => {
            setGenerationError(null)
            setPreview(entry)
            setPreviewIsPartial(true)
          })
        }
        throw new Error('Generation stream closed before done')
      } finally {
        lifetime.signal.removeEventListener('abort', unsubscribe)
        stream.unsubscribe()
      }
    })()),
    onMutate: () => { setGenerationError(null) },
    onError: (error) => {
      if (typeof error === 'object' && error !== null && 'status' in error && error.status === 403) {
        setPreview(null)
        setGenerationError(null)
        void generationAvailability.refetch()
        return
      }
      setGenerationError(error)
    },
  })
  const visiblePreview = preview ?? selectedEntry.data ?? null
  useEffect(() => {
    dismissPreviewRef.current = visiblePreview && !generate.isPending ? () => {
      setPreview(null)
      setSelectedEntryId(null)
      setPreviewIsPartial(false)
      setGenerationError(null)
      setText('')
      setSearchOpen(false)
      navigate({
        to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
        params: pageRouteParams(routerCtx, spaceId, routePageId),
        search: {},
        replace: true,
      })
    } : null
    return () => { dismissPreviewRef.current = null }
  }, [dismissPreviewRef, visiblePreview, generate.isPending, navigate, routerCtx, spaceId, routePageId])
  // Command, progress and Your words belong to the idle Vocabulary page only: never the Language page, a preview,
  // a generation or a linked entry still loading.
  const showOverview = pageKind === 'vocabulary' && !visiblePreview && !generate.isPending &&
    !(selectedEntryId !== null && selectedEntry.isPending)
  const visibleMediaPaths = visiblePreview
    ? entryMediaPaths(visiblePreview)
    : []
  const previewMedia = usePreparedMedia(visibleMediaPaths)

  // A generated entry is judged from the preview, so purging is reachable
  // before adoption — otherwise a bad generation must be adopted just to be
  // deleted, and the next generate would serve the cached copy back.
  async function handlePurgePreview(entry: CachedEntry, purge?: PurgeOptions) {
    const cleaned: string[] = []
    setPurgeNotice(null)
    setPurgeError(null)
    try {
      if (purge?.remote ?? false) {
        const result = await routerCtx.rpcClient.lexicon.purge.mutate({
          id: entry.id,
          remote: purge?.remote ?? true,
        })
        if (result.deleted) cleaned.push('shared dictionary entry')
        if (result.responsesDeleted > 0) {
          cleaned.push(`${result.responsesDeleted} cached model responses`)
        }
        if (result.mediaDeleted > 0) {
          cleaned.push(`${result.mediaDeleted} generated media files`)
        }
      }
      setPreview(null)
      setSelectedEntryId(null)
      // The deleted entry is still addressed by the `preview` search param and
      // still sitting in the getEntry cache, so clearing local state alone
      // renders it straight back. Drop both.
      routerCtx.queryClient.removeQueries(
        routerCtx.rpc.lexicon.getEntry.queryOptions({ id: entry.id }),
      )
      navigate({
        to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
        params: pageRouteParams(routerCtx, spaceId, routePageId),
        search: {},
        replace: true,
      })
      await Promise.all([localSearch.refetch(), remoteSearch.refetch()])
      setPurgeNotice(
        cleaned.length > 0
          ? `Deleted ${entry.headword}: ${cleaned.join(', ')}.`
          : `Nothing to delete for ${entry.headword}.`,
      )
    } catch (error) {
      setPurgeError(error)
    }
  }

  async function openHit(hit: LexiconHit) {
    setOpenError(null)
    try {
      await _openHit(hit)
    } catch (error) {
      setOpenError(error)
    }
  }

  /** The server owns dictionary popularity; the replicated page already contains the adopted entry. */
  function _afterAdopt(pageId: string, entryId: string) {
    const abort = new AbortController()
    const bumped = routerCtx.rpcClient.lexicon.bumpAdoption.mutate({ id: entryId }, { signal: abort.signal })
    const unregister = routerCtx.writes.register(spaceId, () => Promise.resolve(), () => { abort.abort() })
    return bumped.catch(async (error: unknown) => {
      if (!abort.signal.aborted && !(error instanceof TypeError)) await routerCtx.adoptBumpErrors.report(pageId, error)
    }).finally(unregister).catch(() => {})
  }

  async function _openHit(hit: LexiconHit) {
    const adoptedPageId =
      hit.origin === 'pages' ? hit.pageId : adopted.get(hit.entryId)
    if (adoptedPageId) {
      navigate({
        to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
        params: pageRouteParams(routerCtx, spaceId, adoptedPageId),
      })
      return
    }
    const entry = await routerCtx.rpcClient.lexicon.getEntry.query({ id: hit.entryId })
    if (!entry) return
    setSelectedEntryId(entry.id)
    setPreview(entry)
    setPreviewIsPartial(false)
  }

  return (
    <div className="flex w-full flex-col gap-8 px-4 pb-10 md:px-6">
      {purgeError !== null && <ErrorDetails error={purgeError} compact />}
      {openError !== null && <ErrorDetails error={openError} compact />}
      {(deleteNotice ?? purgeNotice) ? (
        <p
          className="text-on-surface-secondary text-sm"
          data-testid="lexicon-delete-notice"
          role="status"
        >
          {deleteNotice ?? purgeNotice}
        </p>
      ) : null}
      {generate.isPending ? (
        <div className="text-on-surface-secondary flex items-center gap-2 text-sm" role="status">
          <LoaderCircleIcon className="size-4 animate-spin" />
          Generating “{text.trim()}”…
        </div>
      ) : null}
      {showOverview ? <div
        className="bg-surface-2 flex w-full flex-col overflow-hidden rounded-2xl border"
        onBlur={(event) => {
          if (!isFocusWithin(event)) {
            setSearchOpen(false)
          }
        }}
      >
        <Command
          items={hits}
          itemToStringValue={(hit) => (hit as LexiconHit).headword}
          mode="none"
          autoHighlight={false}
          {...{
            onItemHighlighted: (value: unknown) => {
              setRowHighlighted(value !== undefined)
            },
          }}
          value={text}
          onValueChange={(value) => {
            setText(value)
            setPreview(null)
            setPreviewIsPartial(false)
            setSelectedEntryId(null)
            setGenerationError(null)
          }}
        >
          <CommandPanel>
            <CommandInput
              data-testid="lexicon-search-input"
              placeholder={t('lexiconSearchPlaceholder')}
              onFocus={() => {
                setSearchOpen(true)
              }}
              onKeyDown={(event) => {
                // :uc[enter-accepts-the-highlighted-row-else-the-typed-word]: base-ui clicks a
                // highlighted row itself; this handler acts only on the typed word.
                if (event.key !== 'Enter' || rowHighlighted) return
                event.preventDefault()
                const exactHit = _findExactLexiconHit(text, hits)
                if (exactHit) {
                  void openHit(exactHit)
                  return
                }
                if (!canGenerate || !text.trim() || generate.isPending) return
                generate.mutate({ text: generationKey })
              }}
            />
            {searchOpen ? (
              <CommandList data-testid="lexicon-search-results">
                <CommandEmpty>
                  {text.trim() ? 'not in the dictionary' : null}
                </CommandEmpty>
                <CommandCollection>
                  {(hit: LexiconHit) => (
                    <CommandItem
                      key={hit.entryId}
                      data-testid={`lexicon-search-result-${hit.entryId}`}
                      value={hit}
                      onClick={() => {
                        void openHit(hit)
                      }}
                    >
                      {hit.origin === 'pages' ? (
                        <BookMarkedIcon className="text-on-surface-secondary size-4 shrink-0" />
                      ) : (
                        <CloudIcon className="text-on-surface-secondary size-4 shrink-0" />
                      )}
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="text-on-surface flex items-baseline gap-1.5 font-medium">
                          {hit.headword}
                        </span>
                        <span className="text-on-surface-secondary truncate text-sm">
                          {[
                            hit.pos,
                            hit.transcription,
                            translationFor(
                              hit.translations,
                              nativeTongueFor(
                                nativeLanguage,
                                routerCtx.defaultNativeLanguage,
                              ),
                            ),
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                      <span className="text-on-surface-secondary shrink-0 text-xs" data-testid={`lexicon-result-action-${hit.entryId}`}>
                        <LexiconHitAction hit={hit} spaceId={spaceId} />
                      </span>
                    </CommandItem>
                  )}
                </CommandCollection>
                {canGenerate && text.trim() && !_hasExactLexiconHit(text, hits) ? (
                  <CommandItem
                    data-testid="lexicon-generate"
                    value={_GENERATE_ITEM}
                    disabled={generate.isPending}
                    onClick={() => {
                      generate.mutate({ text: generationKey })
                    }}
                  >
                    {generate.isPending ? (
                      <LoaderCircleIcon className="text-on-surface-secondary size-4 shrink-0 animate-spin" />
                    ) : (
                      <WandIcon className="text-on-surface-secondary size-4 shrink-0" />
                    )}
                    <span className="text-on-surface font-medium">
                      {generate.isPending
                        ? `Generating "${text.trim()}"…`
                        : `Generate "${text.trim()}"`}
                    </span>
                  </CommandItem>
                ) : null}
              </CommandList>
            ) : null}
          </CommandPanel>
        </Command>
      </div> : null}
        {generationError === null ? null : (
          <div
            className="text-danger border-separator border-t px-4 py-2 text-sm"
            role="alert"
            data-testid="lexicon-generation-error"
          >
            <p>generation failed</p>
            <p>{generationError instanceof Error && generationError.message ? generationError.message : 'Unknown error'}</p>
            {previewIsPartial && visiblePreview ? <p>Generation incomplete. Finish generation before adopting.</p> : null}
            {canGenerate ? <Button onClick={() => { generate.mutate({ text: generationKey }) }}>Try again</Button> : null}
          </div>
        )}


      {visiblePreview && !generate.isPending && !isMobile ? (
        <Button data-testid="lexicon-back-to-vocabulary" variant="text" className="self-start" onClick={() => { dismissPreviewRef.current?.() }}>
          <ChevronLeftIcon />Back to Vocabulary
        </Button>
      ) : null}

      {pendingAdoption ? (
        <section data-testid="lexicon-adoption-pending" aria-busy="true">
          <p role="status">Adding {pendingAdoption.headword}…</p>
          <ImageLoadingProvider pending={previewMedia.pending} onRetry={previewMedia.retry}>
            <EntryEditor spaceId={spaceId} mode="adopted" readOnly entry={pendingAdoption}
              l1={nativeTongueFor(nativeLanguage, routerCtx.defaultNativeLanguage)} resolveMediaSrc={previewMedia.resolve}
              onChange={() => {}} />
          </ImageLoadingProvider>
        </section>
      ) : null}
      {visiblePreview ? (
        <div hidden={pendingAdoption !== null}>
        <ImageLoadingProvider pending={previewMedia.pending} onRetry={previewMedia.retry}>
        <EntryEditor
          spaceId={spaceId}
          mode="preview"
          readOnly={!canWrite}
          onAdopt={canWrite && !previewIsPartial ? async (selectedSenseIds) => {
            setPendingAdoption(adoptEntryContent(visiblePreview, { selectedSenseIds, mintId: ulid }))
            try {
            await routerCtx.writes.flush(spaceId)
            const authoritative = await routerCtx.rpcClient.lexicon.getEntry.query({ id: visiblePreview.id })
            if (!authoritative) throw new Error('Lexicon entry not found')
            const rule = authoritative.kind === 'lexeme'
              ? await routerCtx.rpcClient.lexicon.getRule.query({ language: authoritative.language, key: authoritative.inflectionClass })
              : undefined
            const result = await adoptEntry(
              { writes: routerCtx.writes,
                pageCommands: adoptPageCommands(routerCtx, spaceId),
                db: routerCtx.db(spaceId),
                tel: routerCtx.tel,
                afterCommit: routerCtx.afterCommit,
                afterLocalCommit: routerCtx.afterLocalCommit,
                spaceId,
                userId: user.id,
                deviceId: routerCtx.deviceId,
                now: () => new Date(),
              },
              {
                entry: authoritative,
                selectedSenseIds,
                l1: nativeTongueFor(
                  nativeLanguage,
                  routerCtx.defaultNativeLanguage,
                ),
                ...(rule ? { rule } : {}),
              },
            )
            navigate({
              to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
              params: pageRouteParams(routerCtx, spaceId, result.pageId),
            })
            if (result.created) await _afterAdopt(result.pageId, authoritative.id)
            } catch (error) {
              setPendingAdoption(null)
              throw error
            }
          } : undefined}
          entry={visiblePreview}
          l1={nativeTongueFor(nativeLanguage, routerCtx.defaultNativeLanguage)}
          resolveMediaSrc={previewMedia.resolve}
          onDelete={canWrite ? purge => handlePurgePreview(visiblePreview, purge) : undefined}
          rulePageExists={
            visiblePreview.kind === 'lexeme' &&
            _findRulePageId(
              children,
              loaderData.languagePageId ?? null,
              visiblePreview.inflectionClass,
              rulePages ?? [],
            ) !== undefined
          }
          onOpenRulePage={() => {
            if (visiblePreview.kind !== 'lexeme') return
            const rulePageId = _findRulePageId(
              children,
              loaderData.languagePageId ?? null,
              visiblePreview.inflectionClass,
              rulePages ?? [],
            )
            if (!rulePageId) return
            navigate({
              to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
              params: pageRouteParams(routerCtx, spaceId, rulePageId),
            })
          }}

        />
        </ImageLoadingProvider>
        </div>
      ) : null}

      {showOverview ? <>
      <VocabularyProgress
        workspaceId={spaceId}
        translationLanguage={nativeTongueFor(
          nativeLanguage,
          routerCtx.defaultNativeLanguage,
        )}
        onOpenWord={(word, status) => {
          if (status === 'toLearn') {
            setWordToGenerate(word)
            return
          }
          setText(word)
          setSearchOpen(true)
        }}
      />
      <AdoptedEntriesSection
        pages={children.filter(child => child.parentId === routePageId)}
        workspaceId={spaceId}
        translationLanguage={nativeTongueFor(
          nativeLanguage,
          routerCtx.defaultNativeLanguage,
        )}
      />
      </> : null}
      <Dialog open={wordToGenerate !== null} onOpenChange={open => { if (!open) setWordToGenerate(null) }}>
        <DialogPopup data-testid="lexicon-generation-confirmation">
          <DialogHeader>
            <DialogTitle>Generate “{wordToGenerate}”?</DialogTitle>
            <DialogDescription>
              {canGenerate ? 'Generate this word, then choose the senses you want to learn.' : online ? 'Word generation is unavailable for your account.' : 'Connect to the internet to generate this word.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="lexicon-generation-cancel" variant="text" onClick={() => { setWordToGenerate(null) }}>Cancel</Button>
            <Button data-testid="lexicon-generation-confirm" variant="filled" disabled={!canGenerate || generate.isPending} onClick={() => {
              if (!wordToGenerate || !canGenerate || generate.isPending) return
              const word = wordToGenerate.trim().toLocaleLowerCase()
              setWordToGenerate(null)
              setText(wordToGenerate)
              setSearchOpen(false)
              setPreview(null)
              setPreviewIsPartial(false)
              setSelectedEntryId(null)
              generate.mutate({ text: word })
            }}>Generate</Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </div>
  )
}

function AdoptedEntriesSection(props: {
  pages: PageMeta[]
  workspaceId: string
  translationLanguage: TranslationLanguage
}) {
  const routerCtx = useRouterContext()
  const navigate = useNavigate()
  const { t } = useTranslation('page')
  const { workspaceId } = props
  // Only entries; a language page's children also include its system subpages
  // and ordinary notes, and neither belongs in the words table.
  const entries = props.pages.filter(
    (child) => child.kind === 'lexiconEntry',
  )

  const resource = routerCtx.spaces.get(workspaceId)
  const db = resource.db
  const { data: details } = useQuery(dbQueryOptions(db.query`SELECT ${db.pages.id}, COALESCE(${db.pages.transcription},'') AS transcription,
    COALESCE(${db.pages.translations},'{}') AS translations
    FROM ${db.pages} WHERE ${db.pages.id.inArray(entries.map(entry => entry.id))}`,
    transcriptionRowSchema), resource.queryClient)

  function openEntry(pageId: string) {
    navigate({
      to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
      params: pageRouteParams(routerCtx, workspaceId, pageId),
    })
  }

  return (
    <section className="flex flex-col gap-2" data-testid="lexicon-your-words">
      <h2 className="text-on-surface text-sm font-medium tracking-wide uppercase select-none">
        {t('lexiconYourWords')}
      </h2>
      {entries.length > 0 ? (
        <Table className="border-separator bg-surface-2 rounded-2xl border">
          <TableHeader className="select-none">
            <TableRow>
              <TableHead>{t('lexiconHeadword')}</TableHead>
              <TableHead>{t('lexiconTranscription')}</TableHead>
              <TableHead>{t('lexiconTranslation')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((child, index) => {
              if (child.kind !== 'lexiconEntry') return null
              return (
                <TableRow
                  key={child.id}
                  className="hover:bg-surface-3 focus-visible:ring-primary cursor-pointer select-none focus-visible:ring-2"
                  data-testid={`lexicon-adopted-entry-${child.id}`}
                  data-order={index}
                  role="link"
                  tabIndex={0}
                  onClick={() => {
                    openEntry(child.id)
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    openEntry(child.id)
                  }}
                >
                  <TableCell>{child.title}</TableCell>
                  <TableCell>{details?.find(row => row.id === child.id)?.transcription}</TableCell>
                  <TableCell>{translationFor(details?.find(row => row.id === child.id)?.translations ?? {}, props.translationLanguage)}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      ) : (
        <Empty>
          <EmptyHeader>
            <EmptyMedia aria-hidden="true" className="grid size-32 place-items-center">
              <Shape shape={sunny} className="col-start-1 row-start-1 size-full text-surface-2" />
              <BookMarkedIcon className="text-on-surface-secondary col-start-1 row-start-1 size-12" />
            </EmptyMedia>
            <EmptyTitle>{t('lexiconNoWords')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}
    </section>
  )
}

function VocabularyProgress(props: {
  workspaceId: string
  translationLanguage: TranslationLanguage
  onOpenWord: (word: string, status: LemmaStatus) => void
}) {
  const loaderData = usePageView()
  const frequencyOptions = loaderData.frequencyListOptions
  const adoptedLemmaOptions = loaderData.adoptedLemmaOptions
  if (frequencyOptions && adoptedLemmaOptions && loaderData.languagePageId) {
    return (
      <VocabularyProgressPage
        adoptedLemmaOptions={adoptedLemmaOptions}
        frequencyOptions={frequencyOptions}
        onOpenWord={props.onOpenWord}
        translationLanguage={props.translationLanguage}
        workspaceId={props.workspaceId}
      />
    )
  }
  return null
}

function VocabularyProgressPage(props: {
  adoptedLemmaOptions: Parameters<typeof useSuspenseQuery<AdoptedLemmaRow[]>>[0]
  frequencyOptions: Parameters<typeof useSuspenseQuery<FrequencyRow[]>>[0]
  translationLanguage: TranslationLanguage
  onOpenWord: (word: string, status: LemmaStatus) => void
  workspaceId: string
}) {
  const queryClient = useUserSpaces().get(props.workspaceId).queryClient
  const { data: frequencyList } = useSuspenseQuery(props.frequencyOptions, queryClient)
  const { data: lemmas } = useSuspenseQuery(props.adoptedLemmaOptions, queryClient)
  return (
    <div className="flex h-96 flex-col">
      <VocabularyProgressList
        workspaceId={props.workspaceId}
        progress={vocabularyProgress(lemmas, frequencyList)}
        translationLanguage={props.translationLanguage}
        onOpenWord={props.onOpenWord}
      />
    </div>
  )
}

const _GENERATE_ITEM = { kind: 'generate' } as const

function _findExactLexiconHit(text: string, hits: LexiconHit[]): LexiconHit | undefined {
  const normalized = text.trim().toLocaleLowerCase()
  return hits.find(hit => hit.headword.trim().toLocaleLowerCase() === normalized)
}

function _hasExactLexiconHit(text: string, hits: LexiconHit[]): boolean {
  return _findExactLexiconHit(text, hits) !== undefined
}

function _findRulePageId(
  pages: PageMeta[], parentId: string | null, inflectionClass: string,
  rules: { id: string; parentId: string | null; ruleKeys: string[] }[],
): string | undefined {
  const parent = pages.find(page => page.id === parentId)
  const languagePageId = parent?.kind === 'vocabulary' ? parent.parentId : parentId
  return rules.find(rule => rule.ruleKeys.includes(inflectionClass) && (rule.parentId === languagePageId || pages.some(parent => parent.id === rule.parentId && parent.parentId === languagePageId)))?.id
}

function PageTreeNode(props: {
  item: FlashcardsPageTreeItem
  currentSpaceId: string
  currentParentId: string | null
  currentPageId: string | null
  disabled: boolean
  showActiveState: boolean
  onOpenPage: (pageId: string) => void
  onDeletePage: (pageId: string) => void
  levels: Record<string, number>
  inheritedLevel: number
}) {
  const activeWithin = props.currentSpaceId === props.item.spaceId && ((props.currentPageId !== null && _treeContainsPage(props.item, props.currentPageId)) || (props.currentParentId !== null && _treeContainsPage(props.item, props.currentParentId)))
  const expansion = usePageExpansion()
  const expanded = expansion.expandedPages[props.item.id] ?? (activeWithin && (props.currentPageId !== props.item.id || props.item.kind === 'language'))
  const level = props.levels[props.item.id] ?? props.inheritedLevel
  const hasChildren = props.item.children.length > 0
  const isActive =
    props.showActiveState && props.currentSpaceId === props.item.spaceId && props.currentPageId === props.item.id

  return (
    <SidebarMenuItem>
      <Collapsible open={expanded} onOpenChange={expanded => { expansion.toggle(props.item.id, expanded) }}>
        <div className="group/page-node relative flex min-w-0 items-center">
          {hasChildren ? (
            <CollapsibleTrigger
              render={
                <IconButton
                  aria-label={`Toggle ${props.item.title}`}
                  className="peer/page-toggle hover:bg-primary/8 size-7 shrink-0 rounded-full transition-opacity md:absolute md:top-1/2 md:left-0 md:z-10 md:-translate-y-1/2 md:opacity-0 md:group-hover/page-node:opacity-100 md:focus-visible:opacity-100"
                  data-testid={`flashcards-page-toggle-${props.item.id}`}
                  semanticRole="secondary"
                  size="sm"
                  type="button"
                  variant="text"
                >
                  <ChevronRightIcon
                    className={`size-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
                  />
                  {`Toggle ${props.item.title}`}
                </IconButton>
              }
            />
          ) : (
            <span className="w-7 shrink-0 md:hidden" />
          )}
          <div
            className={`flex min-w-0 flex-1 items-center rounded-full transition-colors hover:bg-primary/8 active:bg-surface-3 ${
              isActive ? 'bg-primary/8' : ''
            }`}
          >
            <SidebarMenuButton
              aria-current={
                props.currentSpaceId === props.item.spaceId && props.currentPageId === props.item.id ? 'page' : undefined
              }
              className="min-w-0 flex-1 rounded-full bg-transparent px-1.5 hover:bg-transparent active:bg-transparent data-[active=true]:bg-transparent data-[state=open]:hover:bg-transparent"
              data-testid={`flashcards-page-link-${props.item.id}`}
              isActive={isActive}
              size="sm"
              onClick={() => {
                props.onOpenPage(props.item.id)
              }}
            >
              <FileTextIcon
                className={
                  hasChildren
                    ? 'transition-opacity md:group-hover/page-node:opacity-0 md:peer-focus-visible/page-toggle:opacity-0'
                    : undefined
                }
              />
              <span>{props.item.title}</span>
            </SidebarMenuButton>
            <Menu>
              <MenuTrigger render={<IconButton aria-label={`More actions for ${props.item.title}`} className="hover:bg-primary/8 size-7 shrink-0 rounded-full opacity-100 transition-opacity md:opacity-0 md:group-hover/page-node:opacity-100 focus-visible:opacity-100 data-popup-open:opacity-100" data-testid={`flashcards-page-more-${props.item.id}`} disabled={props.disabled} semanticRole="secondary" size="sm" type="button" variant="text" />}>
                <MoreHorizontalIcon />
                More actions
              </MenuTrigger>
              <MenuPopup align="end">
                {props.item.parentId !== null && props.item.kind !== 'personalRoot' && level >= 2 ? <MenuItem className="data-highlighted:bg-error/8 data-highlighted:text-error" data-testid={`flashcards-page-delete-${props.item.id}`} onClick={() => { props.onDeletePage(props.item.id) }}><Trash2Icon />Trash</MenuItem> : null}
              </MenuPopup>
            </Menu>
          </div>
        </div>
        {hasChildren ? (
          <CollapsibleContent>
            <SidebarMenuSub className="mr-0 gap-0.5 pr-0">
              {props.item.children.map((child) => (
                <PageTreeNode
                  key={child.id}
                  item={child}
                  currentSpaceId={props.currentSpaceId}
                  currentParentId={props.currentParentId}
                  currentPageId={props.currentPageId}
                  disabled={props.disabled}
                  showActiveState={props.showActiveState}
                  onOpenPage={props.onOpenPage}
                  onDeletePage={props.onDeletePage}
                  levels={props.levels}
                  inheritedLevel={level}
                />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        ) : null}
      </Collapsible>
    </SidebarMenuItem>
  )
}

function _treeContainsPage(
  item: FlashcardsPageTreeItem,
  pageId: string,
): boolean {
  if (item.id === pageId) return true
  return item.children.some((child) => _treeContainsPage(child, pageId))
}

export function buildPageTree(pages: PageMeta[], placements: Array<{ pageId: string; parentPageId: string }> ): FlashcardsPageTreeItem[] {
  const ordinaryPages = pages
  return [...new Set(ordinaryPages.map(page => page.spaceId))].flatMap(spaceId => _buildSpaceTree(ordinaryPages.filter(page => page.spaceId === spaceId), placements))
}

function _buildSpaceTree(pages: PageMeta[], placements: Array<{ pageId: string; parentPageId: string }>) {
  const parents = new Map(placements.map(edge => [edge.pageId, edge.parentPageId]))
  function sectionParent(page: PageMeta) {
    let parent = page.parentId
    const seen = new Set<string>()
    while (parent && !pages.some(candidate => candidate.id === parent) && parents.has(parent) && !seen.has(parent)) {
      seen.add(parent)
      parent = parents.get(parent) ?? null
    }
    return parent
  }
  const pagesByParent = new Map<string | null, PageMeta[]>()
  for (const page of pages) {
    const parentId = pages.some(parent => parent.id === page.parentId) ? page.parentId : null
    const siblings = pagesByParent.get(parentId) ?? []
    siblings.push(page)
    pagesByParent.set(parentId, siblings)
  }
  const build = (parentId: string | null): FlashcardsPageTreeItem[] => {
    return (pagesByParent.get(parentId) ?? []).map((page) => ({
      id: page.id,
      kind: page.kind,
      spaceId: page.spaceId,
      title: _getPageTitle(page),
      parentId: sectionParent(page),
      // The walk STOPS at a kind whose policy says so. The node itself is
      // still rendered — it is a real page a learner can open — but its
      // subtree is never entered, which is what keeps thousands of entries
      // out of the sidebar. Ordinary notes under the same language page keep
      // their `walk` policy and are unaffected.
      children: pageKindPolicy(page.kind).nav === 'stop' ? [] : build(page.id),
    }))
  }
  return build(null)
}

/** Trash commit shared by the page tree and the page header menu; trashing the open page opens the first other tree page. */
async function _trashPage(deps: {
  routerCtx: FlashcardsFeRouterCtx; navigate: ReturnType<typeof useNavigate>; flush: () => Promise<unknown>
  openPage: (pageId: string, spaceId: string) => Promise<void>; tree: FlashcardsPageTreeItem[]; workspaceId: string; open: boolean
}, input: { pageId: string; spaceId: string }) {
  await deps.flush()
  const db = deps.routerCtx.db(input.spaceId)
  const row = await db.query`SELECT ${db.pages.data} FROM ${db.pages} WHERE ${db.pages.id.eq(input.pageId)}`.executeOneOrThrow({ data: db.pages.data })
  await commitWorkspacePageEdit(deps.routerCtx, input.spaceId, { pageId: input.pageId, frame: row.data, fields: { deletedAt: Date.now() }, now: new Date() })
  if (!deps.open) return
  const next = deps.tree.flatMap(function flatten(item): FlashcardsPageTreeItem[] { return [item, ...item.children.flatMap(flatten)] }).find(item => item.id !== input.pageId && item.kind !== 'personalRoot')
  if (next) await deps.openPage(next.id, next.spaceId)
  else deps.navigate({ to: '/$workspaceId/spaces/$spaceId/pages', params: { workspaceId: deps.workspaceId, spaceId: input.spaceId } })
}

function _getPageTitle(page: { title: string }) {
  const title = page.title.trim()
  return title.length > 0 ? title : 'Untitled page'
}

function _resolvePage(pages: PageMeta[], ref: PageRef) {
  const page = pages.find((candidate) => candidate.id === ref.pageId && candidate.spaceId === ref.spaceId)
  if (!page) return { label: 'Page unavailable' }
  return { label: _getPageTitle(page) }
}

function LexiconHitAction({ hit, spaceId }: { hit: LexiconHit; spaceId: string }) {
  const ctx = useRouterContext()
  const resource = ctx.spaces.get(spaceId)
  const db = resource.db
  const pageId = hit.origin === 'pages' ? hit.pageId ?? '' : ''
  const counts = useQuery(dbQueryOptions(db.query`
    WITH senses AS (SELECT json_extract(crdt_json(${db.pages.data}), '$.content.entry.senses') AS value,
      ${db.pages}.rowid AS pageRowid FROM ${db.pages} WHERE ${db.pages.id.eq(pageId)})
    SELECT COUNT(*) AS total, COALESCE(SUM(EXISTS(SELECT 1 FROM crdt_attr_lookup('idx_pages_crdt_attr',
      json_extract(sense.value,'$.senseRecallQuestionId'),senses.pageRowid) parts
      WHERE json_extract(parts.fields,'$.qid')=json_extract(sense.value,'$.senseRecallQuestionId'))),0) AS selected
    FROM senses,json_each(senses.value) sense
  `, s.object({ total: s.number(), selected: s.number() }), { enabled: hit.origin === 'pages' }), resource.queryClient)
  if (hit.origin !== 'pages') return 'Add · instant, free'
  const row = counts.data?.[0]
  return row ? `Open · ${row.selected}/${row.total} senses` : 'Open'
}

/** Names a new workspace; a failed create keeps the form and its name, the current page and the directory. */
function NewWorkspaceForm({ onCreate, onDone }: { onCreate: (name: string) => Promise<void>; onDone: () => void }) {
  const [name, setName] = useState('')
  // isPending updates on the next render; two submits in one event loop turn both see false.
  const submitting = useRef(false)
  const create = useMutation({ mutationFn: onCreate, onSuccess: onDone, onSettled: () => { submitting.current = false } })
  return (
    <form
      className="flex flex-col gap-2 px-2"
      data-testid="flashcards-new-workspace-form"
      onSubmit={event => {
        event.preventDefault()
        if (submitting.current) return
        submitting.current = true
        create.mutate(name.trim())
      }}
    >
      <label htmlFor="flashcards-new-workspace-name" className="flex flex-col gap-1 text-xs font-medium">
        Workspace name
        <Input id="flashcards-new-workspace-name" data-testid="flashcards-new-workspace-name" required value={name} onChange={event => { setName(event.target.value) }} />
      </label>
      <div className="flex gap-2">
        <Button data-testid="flashcards-new-workspace-submit" type="submit" variant="filled" size="sm" disabled={!name.trim() || create.isPending}>{create.isPending ? 'Creating…' : 'Create workspace'}</Button>
        <Button type="button" variant="text" size="sm" disabled={create.isPending} onClick={onDone}>Cancel</Button>
      </div>
      {create.error ? <div role="alert" className="text-error text-xs"><p>Unable to create workspace</p><ErrorDetails error={create.error} compact /></div> : null}
    </form>
  )
}

/** Shared navigation for page and custom database routes. */
type PageErrors = ReturnType<typeof usePageErrors>
type PageRouterContext = ReturnType<typeof useRouterContext>

function _flushPage(writes: ReturnType<typeof usePageWrites>, routerCtx: PageRouterContext, spaceId: string) {
  return writes.settled(() => routerCtx.writes.flush(spaceId))
}

async function _openPage(env: { routerCtx: PageRouterContext; navigate: ReturnType<typeof useNavigate>; report: PageErrors['report']; flush: () => Promise<unknown>; workspaceId: string },
  pageId: string, spaceId: string) {
  await env.flush()
  if (!contentResources(env.routerCtx.spaces.snapshot(env.workspaceId)).some(value => value.space.id === spaceId)) {
    env.report('reference', true)
    return
  }
  env.report('reference')
  env.navigate({
    to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
    params: pageRouteParams(env.routerCtx, spaceId, pageId),
  })
}

/** The open page's navigation. Its own component, fed values rather than the page's closures: inside the 2,000-line page
 * component React Compiler memoized none of these handlers, so every page row change (a remote edit) re-rendered the
 * whole sidebar with new handler identities. */
function PageNavigation({ workspaceMenu, resource, resources, workspaces, tree, pageId, pageSpaceId, parentId, workspaceId, teamspaces, dueCount,
  invitationCount, userId, pending, createPage: createPageCommit, errors, saveError, report, track, writes, beginWorkspaceSelection, editorHandleRef }: {
  workspaceMenu: { open: boolean; setOpen: (open: boolean) => void }
  resource: SpaceResource
  resources: SpaceResource[]
  workspaces: { id: string; name: string }[]
  tree: FlashcardsPageTreeItem[]
  pageId: string
  pageSpaceId: string
  parentId: string | null
  /** The open page's space. */
  workspaceId: string
  teamspaces: { id: string; name: string }[]
  dueCount: number
  invitationCount: number
  userId: string
  pending: boolean
  createPage: (input: { workspaceId: string; parentId?: string | null; teamspaceId?: string; title?: string; alreadyNavigated?: boolean; signal?: AbortSignal }) => Promise<{ pageId: string } | undefined>
  errors: PageErrors['errors']
  saveError: unknown
  report: PageErrors['report']
  track: PageErrors['track']
  writes: ReturnType<typeof usePageWrites>
  beginWorkspaceSelection: () => AbortController
  editorHandleRef: React.RefObject<BlockEditorHandle | null>
}) {
  const routerCtx = useRouterContext()
  const navigate = useNavigate()
  const signOut = useSignOut()

  function flushPagePatches() {
    return _flushPage(writes, routerCtx, pageSpaceId)
  }

  async function handleSignOut() {
    // Aborts a pending selection or create, so a late result cannot open its workspace.
    beginWorkspaceSelection().abort()
    try {
      report('signOut')
      await signOut()
      navigate({ to: '/login' })
    } catch (error) { report('signOut', error) }
  }

  function handleOpenPage(openedPageId: string, requestedSpaceId?: string) {
    return _openPage({ routerCtx, navigate, report, flush: flushPagePatches, workspaceId: resource.space.workspaceId }, openedPageId, requestedSpaceId ?? workspaceId)
  }

  // Each handler is declared before the handlers that call it: React Compiler skips a component that references a
  // function declaration before it (PruneHoistedContexts), and then memoizes none of its handlers.
  async function createAcceptedPage(input: {
    workspaceId: string; parentId: string | null; teamspaceId?: string; title: string; signal?: AbortSignal
  }) {
    const result = await track('createPage', createPageCommit({ ...input, alreadyNavigated: true }))
    if (!result || input.signal?.aborted) return
    const { pageId } = result
    navigate({
      to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
      params: pageRouteParams(routerCtx, input.workspaceId, pageId),
      search: { focusTitle: pageId },
    })
    schedulePageTitleFocus(editorHandleRef)
  }

  function createPage(input: {
    workspaceId: string
    parentId: string | null
    teamspaceId?: string
    title: string
    signal?: AbortSignal
  }) {
    return createAcceptedPage(input)
  }

  async function handleOpenTraining() {
    await flushPagePatches()
    // Birth goes through the training loader's redirect (QUEUE.md §4.2), which
    // lands on the queue head. Jumping straight to a card would skip birth and
    // leave the sitting with no queue at all.
    navigate({
      to: '/$workspaceId/training',
      params: {
        workspaceId: resource.space.workspaceId,
      },
      search: {
        total: String(dueCount),
      },
    })
  }

  async function openWorkspaceSelection(workspaceId: string, selection = beginWorkspaceSelection()) {
    await flushPagePatches()
    if (selection.signal.aborted) return
    await routerCtx.openWorkspace?.(workspaceId)
    if (selection.signal.aborted) return
    // Revocation is only known after the server round trip; offline or transient sync errors keep the local switch.
    await routerCtx.spaces.trigger(workspaceId, true, routerCtx.tel).catch(() => {})
    if (selection.signal.aborted) return
    const syncStatus = await routerCtx.spaces.syncStatusOptions(workspaceId).queryFn()
    if (syncStatus.error instanceof ReplicaTransportError && syncStatus.error.status === 403) throw syncStatus.error
    const spaces = contentResources(routerCtx.spaces.snapshot(workspaceId))
    const rows = await Promise.all(spaces.map(value => value.queryClient.fetchQuery(spaceTreeOptions(value))))
    if (selection.signal.aborted) return
    const nextPage = rows.flat().find(page => !teamspaces.some(team => team.id === page.id))
    if (nextPage) {
      navigate({ to: '/$workspaceId/spaces/$spaceId/pages/$pageId', params: pageRouteParams(routerCtx, nextPage.spaceId, nextPage.id) })
      return
    }
    const own = spaces.find(value => value.space.kind === 'private' && value.space.ownerUserId === userId)
    if (!own) throw new Error('Private space unavailable')
    await createPage({ workspaceId: own.space.id, parentId: null, title: 'Untitled page', signal: selection.signal })
  }

  async function handleWorkspaceChange(workspaceId: string, selection?: AbortController) {
    report('workspace')
    try {
      await openWorkspaceSelection(workspaceId, selection)
    } catch (error) {
      // A failed selection (e.g. membership revoked) keeps the current page.
      if (!selection?.signal.aborted) report('workspace', error)
    }
  }

  async function createAndOpenWorkspace(name: string, selection: AbortController) {
    let workspaceId: string
    try {
      ({ workspaceId } = await routerCtx.rpcClient.workspace.create.mutate({ name }))
    } catch (error) {
      // Committed on the server: report the refresh failure, never a failed create.
      if (!(error instanceof DirectoryRefreshError)) throw error
      if (!selection.signal.aborted) report('workspace', error.cause)
      return
    }
    if (selection.signal.aborted) return
    await handleWorkspaceChange(workspaceId, selection)
  }

  /** The selection starts before the RPC, so a later choice or sign-out aborts it and the late result only joins the directory. */
  async function handleCreateWorkspace(name: string) {
    const selection = beginWorkspaceSelection()
    // A navigation until the whole create and open settles wins; the committed workspace only joins the directory.
    const navigated = abortOnNavigation(selection)
    // `.finally` instead of try/finally, which React Compiler does not support.
    await createAndOpenWorkspace(name, selection).finally(() => { navigated.stop() })
  }

  return <AppNavigation workspaceMenu={workspaceMenu} resource={resource} resources={resources} workspaces={workspaces}
    tree={tree} currentPage={{ id: pageId, spaceId: pageSpaceId, parentId }}
    teamspaces={teamspaces} dueCount={dueCount} invitationCount={invitationCount} pending={pending}
    errors={{ signOut: errors.signOut === undefined ? null : unknownErrorToError(errors.signOut), save: saveError === undefined ? null : unknownErrorToError(saveError), workspace: errors.workspace }}
    onWorkspaceChange={handleWorkspaceChange} onCreateWorkspace={handleCreateWorkspace} onSignOut={handleSignOut} onOpenTraining={handleOpenTraining}
    onCreatePage={createPage} onOpenPage={handleOpenPage} beforeCreate={flushPagePatches} />
}

export function AppNavigation({ resource, resources, workspaces, tree, currentPage, teamspaces, dueCount, invitationCount,
  pending, errors, onWorkspaceChange, onCreateWorkspace, onSignOut, onOpenTraining, onCreatePage, onOpenPage, beforeCreate, workspaceMenu,
}: {
  workspaceMenu?: { open: boolean; setOpen: (open: boolean) => void }
  resource: SpaceResource
  resources: SpaceResource[]
  workspaces: { id: string; name: string }[]
  tree: FlashcardsPageTreeItem[]
  currentPage?: { id: string; spaceId: string; parentId: string | null }
  teamspaces: { id: string; name: string }[]
  dueCount: number
  invitationCount: number
  pending: boolean
  errors: { signOut?: Error | null; save?: Error | null; workspace?: unknown }
  onWorkspaceChange: (id: string) => Promise<void>
  onCreateWorkspace: (name: string) => Promise<void>
  onSignOut: () => Promise<void>
  onOpenTraining: () => Promise<void>
  onCreatePage: (input: { workspaceId: string; parentId: string | null; teamspaceId?: string; title: string }) => Promise<void>
  onOpenPage: (pageId: string, spaceId: string) => Promise<void>
  beforeCreate: () => Promise<void>
}) {
  const navigate = useNavigate()
  const routerCtx = useRouterContext()
  const { userId } = useUserSpaces()
  const isMobile = useIsMobile()
  const { t } = useTranslation('page')
  const { data: capabilities } = useQuery(routerCtx.rpc.page.levels.queryOptions({ workspaceId: resource.space.workspaceId }), routerCtx.queryClient)
  const deletePage = useMutation({
    mutationFn: (input: { pageId: string; spaceId: string }) => _trashPage({ routerCtx, navigate, flush: beforeCreate, openPage: onOpenPage, tree, workspaceId: resource.space.workspaceId,
      open: currentPage?.id === input.pageId && currentPage.spaceId === input.spaceId }, input),
  })
  const [creatingWorkspace, setCreatingWorkspace] = useState(false)
  const workspaceName = workspaces.find(workspace => workspace.id === resource.space.workspaceId)?.name ?? ''
  function createRootPage() {
    const own = contentResources(routerCtx.spaces.snapshot(resource.space.workspaceId)).find(value => value.space.kind === 'private' && value.space.ownerUserId === userId)
    if (!own) throw new Error('Private space unavailable')
    void onCreatePage({ workspaceId: own.space.id, parentId: null, title: 'Untitled page' })
  }
  return (
    <>
      <SidebarHeader
        className="gap-3 mb-4"
        data-testid={
          isMobile ? 'flashcards-mobile-navigation-header' : 'flashcards-sidebar'
        }
      >
        {/* Notion's sidebar header: workspace icon and name over the sync state, with Sync now beside the menu chevron (flashcards-editor-home.requirements.md 2026-11-02). */}
        <div className="flex min-w-0 items-center gap-1">
        <Menu open={workspaceMenu?.open} onOpenChange={workspaceMenu?.setOpen}>
          <MenuTrigger
            className="hover:bg-primary/8 data-popup-open:bg-primary/8 flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-left"
            data-testid="flashcards-workspace-menu"
            aria-label="Workspace and account"
          >
            <Avatar size="sm" className="rounded-lg shrink-0" name={workspaceName} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-semibold" data-testid="flashcards-workspace-name">{workspaceName}</span>
              <WorkspaceSyncLine className="text-on-surface-secondary truncate text-xs" workspaceId={resource.space.workspaceId} unsaved={Boolean(errors.save)} />
            </span>
            <ChevronDownIcon className="size-4 shrink-0" />
          </MenuTrigger>
          <MenuPopup align="start" className="w-(--anchor-width) min-w-56 max-w-[calc(100vw-2rem)]">
            <MenuRadioGroup aria-label="Workspaces" value={resource.space.workspaceId} onValueChange={(value: string) => { void onWorkspaceChange(value) }}>
              {workspaces.map(workspace => (
                <MenuRadioItem key={workspace.id} value={workspace.id} data-testid={`flashcards-workspace-${workspace.id}`}>
                  <span className="truncate">{workspace.name}</span>
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
            <MenuItem data-testid="flashcards-new-workspace" onClick={() => { setCreatingWorkspace(true) }}>
              <PlusIcon />New workspace
            </MenuItem>
            <MenuSeparator />
            <MenuGroup>
              <MenuItem data-testid="flashcards-open-settings" onClick={() => { navigate({ to: '/settings' }) }}>
                <SettingsIcon />{t('settingsButton')}
              </MenuItem>
              <MenuItem data-testid="flashcards-open-trash" onClick={() => { navigate({ to: '/$workspaceId/trash', params: { workspaceId: resource.space.workspaceId } }) }}>
                <Trash2Icon />Trash
              </MenuItem>
              <MenuItem disabled={pending} onClick={() => { void onSignOut() }}>
                <LogOutIcon />Sign out
              </MenuItem>
            </MenuGroup>
          </MenuPopup>
        </Menu>
        <WorkspaceSyncButton workspaceId={resource.space.workspaceId} className="size-8" />
        </div>
        {creatingWorkspace ? <NewWorkspaceForm onCreate={onCreateWorkspace} onDone={() => { setCreatingWorkspace(false) }} /> : null}
        {errors.signOut ? <p role="alert">Sign out failed: {errors.signOut.message}</p> : null}
        {errors.workspace !== null && errors.workspace !== undefined && (
          <div role="alert" className="text-error px-2 text-xs">
            <p>Unable to open workspace</p>
            <ErrorDetails error={errors.workspace} compact />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="gap-8 medium:-mx-4 medium:px-4">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              <WorkspaceSearch key={`${userId}:${resource.space.workspaceId}`} resources={resources} workspaceId={resource.space.workspaceId} currentPage={currentPage} onOpenPage={onOpenPage} />
              <SidebarMenuItem>
                <SidebarMenuButton
                  className="rounded-lg"
                  data-testid="flashcards-open-cards"
                  size="sm"
                  onClick={() => {
                    navigate({
                      to: '/$workspaceId/cards',
                      params: { workspaceId: resource.space.workspaceId },
                    })
                  }}
                >
                  <LayersIcon />
                  <span>{t('cardsButton')}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {dueCount > 0 ? (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    className="rounded-lg"
                    data-testid="flashcards-open-training"
                    disabled={pending}
                    size="sm"
                    onClick={() => {
                      void onOpenTraining()
                    }}
                  >
                    <BookMarkedIcon />
                    <span>
                      {t('trainingButton')}
                      <span className="sr-only">{` (${dueCount})`}</span>
                    </span>
                    <span
                      aria-hidden="true"
                      className="bg-surface-3 text-on-surface-secondary ml-auto min-w-5 rounded-full px-1.5 py-0.5 text-center text-xs font-medium tabular-nums"
                    >
                      {dueCount}
                    </span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : null}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <nav
          aria-label={t('pagesLabel')}
          data-testid="flashcards-page-tree"
          className="flex flex-col gap-4"
        >
          {[{ id: 'private', name: 'Private', rootId: resource.space.personalRootId, teamspaceId: undefined },
            ...teamspaces.map(team => ({ id: team.id, name: team.name, rootId: team.id, teamspaceId: team.id })),
            { id: 'shared', name: 'Shared', rootId: '', teamspaceId: '' }]
            .map(section => {
              const value = { ...resource, space: { ...resource.space, id: section.id, name: section.name } }
              const isPrivate = section.teamspaceId === undefined
              const roots = tree.filter(item => section.id === 'shared' ? item.parentId !== resource.space.personalRootId && !teamspaces.some(team => item.parentId === team.id) && item.parentId !== null : isPrivate ? !teamspaces.some(team => team.id === item.id) && (item.parentId === section.rootId || !item.parentId) : item.id === section.rootId || item.parentId === section.rootId)
              if (section.id === 'shared' && roots.length === 0) return null
              return (
                <SidebarGroup
                  key={section.id}
                  className="py-0"
                  data-testid={`flashcards-space-tree-${isPrivate ? resource.space.id : section.id}`}
                >
                  {!isPrivate &&
                  value.space.id ===
                    resources.find((item) => item.space.kind === 'shared')?.space.id ? (
                    <p className="text-on-surface-secondary px-2 pb-2 text-xs">Shared</p>
                  ) : null}
                  <div className="flex items-center gap-1">
                    <SidebarGroupLabel className="mb-0! min-w-0 flex-1 justify-start gap-2 px-2">
                      {isPrivate ? (
                        <LockIcon className="size-3.5 shrink-0" />
                      ) : (
                        <UsersIcon className="size-3.5 shrink-0" />
                      )}
                      <span
                        className="truncate"
                        title={isPrivate ? 'Private · Only you' : value.space.name}
                      >
                        {isPrivate ? 'Private' : value.space.name}
                      </span>
                    </SidebarGroupLabel>
                    {isPrivate || section.id === 'shared' ? null : (
                      <IconButton
                        aria-label={`Manage ${value.space.name}`}
                        data-testid={`flashcards-manage-space-${value.space.id}`}
                        className="size-6"
                        variant="text"
                        semanticRole="secondary"
                        size="sm"
                        onClick={() => {
                          navigate({
                            to: '/$workspaceId/settings',
                            params: { workspaceId: resource.space.workspaceId },
                            search: { section: 'spaces', spaceId: value.space.id },
                          })
                        }}
                      >
                        <SettingsIcon />
                        Manage space
                      </IconButton>
                    )}
                    {section.id === 'shared' ? null : <SidebarGroupAction
                      className="static"
                      data-testid={
                        isPrivate
                          ? 'flashcards-create-root-page'
                          : `flashcards-create-space-page-${value.space.id}`
                      }
                      render={
                        <IconButton
                          aria-label={
                            isPrivate
                              ? 'New private page'
                              : `New page in ${value.space.name}`
                          }
                          className="size-7 rounded-md"
                          size="sm"
                        />
                      }
                      disabled={pending}
                      onClick={() => {
                        if (isPrivate) createRootPage()
                        else
                          void onCreatePage({
                            workspaceId: resource.space.id,
                            parentId: section.rootId,
                            teamspaceId: section.teamspaceId,
                            title: 'Untitled page',
                          })
                      }}
                    >
                      <PlusIcon />
                      {t('newPage')}
                    </SidebarGroupAction>}
                  </div>
                  <SidebarGroupContent>
                    {roots.length > 0 ? (
                      <SidebarMenu className="gap-0.5">
                        {roots.map((item) => (
                          <PageTreeNode
                            key={JSON.stringify([item.spaceId, item.id])}
                            item={item}
                            currentSpaceId={currentPage?.spaceId ?? resource.space.id}
                            currentParentId={currentPage?.parentId ?? null}
                            currentPageId={currentPage?.id ?? null}
                            disabled={pending || deletePage.isPending}
                            levels={capabilities?.levels ?? {}}
                            inheritedLevel={capabilities?.levels[item.parentId ?? ''] ?? 0}
                            onDeletePage={(pageId) => { deletePage.mutate({ pageId, spaceId: item.spaceId }) }}
                            showActiveState={!isMobile}
                            onOpenPage={(pageId) => {
                              void onOpenPage(pageId, item.spaceId)
                            }}
                          />
                        ))}
                      </SidebarMenu>
                    ) : (
                      <p className="text-on-surface-secondary px-2 py-2 text-xs">
                        {isPrivate ? 'Your private pages appear here.' : 'No pages yet.'}
                      </p>
                    )}
                  </SidebarGroupContent>
                </SidebarGroup>
              )
            })}
          {deletePage.error ? <div role="alert"><p>Unable to delete page</p><ErrorDetails error={unknownErrorToError(deletePage.error)} compact /></div> : null}
          <DatabaseSidebar resource={resource} />
          <aside aria-label="Unavailable pages" className="flex flex-col gap-2">
            <FailedPageItems resource={resource} />
          </aside>
        </nav>
      </SidebarContent>

      {invitationCount > 0 && <SidebarFooter className="border-separator border-t pt-3">
        <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                data-testid="flashcards-invitations"
                size="sm"
                onClick={() => {
                  navigate({
                    to: '/$workspaceId/settings',
                    params: { workspaceId: resource.space.workspaceId },
                    search: { section: 'invitations' },
                  })
                }}
              >
                <UsersIcon />
                <span>Invitations</span>
                <span className="bg-surface-3 ml-auto rounded-full px-2 text-xs">
                  {invitationCount}
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>}
    </>
  )

}
